import LinkedAccountsSection from '@/components/LinkedAccountsSection';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import SigmaPlansManager from '@/components/SigmaPlansManager';
import { 
  Plus, Search, Users, Phone, Calendar, RefreshCw, 
  Trash2, Edit, Settings, Clock, Filter, AlertCircle,
  CheckCircle, XCircle, DollarSign, MessageSquare,
  ChevronLeft, ChevronRight, Send, Megaphone, Sparkles, Link2,
  Pause, Play, Square, Loader2, Bell, Download, X, Package, ArrowLeft, GripVertical
} from 'lucide-react';
import ImageSelector from '@/components/ImageSelector';
import ImportCustomerDialog from '@/components/ImportCustomerDialog';
import LinkCustomerDialog from '@/components/LinkCustomerDialog';
import CustomerGrowthChart from '@/components/CustomerGrowthChart';

const ACTION_COLORS = {
  purple: 'text-purple-500 hover:bg-purple-500/10 border-purple-500/20',
  blue: 'text-blue-500 hover:bg-blue-500/10 border-blue-500/20',
  sky: 'text-sky-500 hover:bg-sky-500/10 border-sky-500/20',
  green: 'text-green-500 hover:bg-green-500/10 border-green-500/20',
  red: 'text-red-500 hover:bg-red-500/10 border-red-500/20',
  default: 'text-foreground/70 hover:bg-muted border-border/40',
};

// Botão de ação: só o ícone, com tooltip nativo (title)
function ActionBtn({ icon: Icon, label, color = 'default', onClick, loading = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      title={label}
      className={`h-8 w-8 rounded-md border bg-transparent inline-flex items-center justify-center ${ACTION_COLORS[color] || ACTION_COLORS.default} ${loading ? 'opacity-60' : ''}`}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
    </button>
  );
}

// Campo de imagem da cobrança: mostra preview, upload pro imagem.app, ou limpar
function BillingImageField({ label, field, settings, setSettings, uploadImg, small }) {
  const [busy, setBusy] = useState(false);
  const val = settings[field] || '';
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const url = await uploadImg(file);
    if (url) setSettings((s) => ({ ...s, [field]: url }));
    setBusy(false);
    e.target.value = '';
  };
  return (
    <div className={`rounded-lg border border-border bg-background/50 p-2 ${small ? '' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">{label}</span>
        {val && <button onClick={() => setSettings((s) => ({ ...s, [field]: '' }))} className="text-[10px] text-red-500">remover</button>}
      </div>
      {val ? (
        <img src={val} alt="" className="w-full h-16 object-cover rounded mt-1" />
      ) : (
        <label className="mt-1 flex items-center justify-center h-16 rounded border border-dashed border-border cursor-pointer hover:bg-muted/40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : <span className="text-[11px] text-muted-foreground">+ enviar imagem</span>}
          <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={busy} />
        </label>
      )}
    </div>
  );
}

export default function CustomersPage() {
  const { user, api } = useAuthStore();
  const location = useLocation();
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const q = params.get('q');
      if (q) setSearchTerm(q);
      setIntegrationFilter(params.get('integration') || '');
      if (params.get('nexu_filter')) {
        try { setNexuFilterIds(JSON.parse(sessionStorage.getItem('nexu_filter_ids') || '[]')); } catch { setNexuFilterIds(null); }
      }
    } catch { /* ignore */ }
  }, [location.search]);
  const isSimple = user?.role === 'simple';

  // Filtra planos conforme o cliente: de integração mostra só os planos daquela integração;
  // cliente normal mostra só planos sem integração.
  const provOf = (integId) => panelIntegrations.find(i => i.id === integId)?.provider;
  const packagesEndpoint = (integId) => provOf(integId) === 'dominus' ? `/sigma/login/${integId}/panel-packages` : `/panel-integrations/${integId}/packages`;
  const plansForCustomer = (cust) => {
    const active = plans.filter(p => p.active !== false);
    const isSigma = !!(cust?.sigma_login_integration_id || cust?.sigma_package_id || cust?.sigma_username || cust?.import_source === 'sigma_login');
    if (!isSigma) return active.filter(p => !p.sigma_login_integration_id && !p.sigma_package_id);

    const integId = cust?.sigma_login_integration_id;
    const cliProv = integId ? provOf(integId) : (cust?.sigma_package_id || cust?.sigma_username ? 'sigma' : null);

    // compatível = mesma integração do cliente, ou plano token quando cliente é sigma, ou mesmo provider
    const compat = (p) => {
      // SÓ os planos vinculados à MESMA integração do cliente
      if (integId && p.sigma_login_integration_id === integId) return true;
      // planos token ainda não migrados: só para cliente sigma que ainda não tem integração-login
      if (!integId && p.sigma_package_id && cliProv === 'sigma') return true;
      return false;
    };
    const compativeis = active.filter(compat);

    // se há marcados "no link" ENTRE OS COMPATÍVEIS, mostra só esses; senão todos os compatíveis
    const marcados = compativeis.filter(p => p.show_in_payment === true);
    if (marcados.length) return marcados;
    if (compativeis.length) return compativeis;
    // fallback: se cliente é sigma, planos token
    if (cliProv === 'sigma') return active.filter(p => p.sigma_package_id);
    return compativeis;
  };
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [connections, setConnections] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState(false);
  const [originFilter, setOriginFilter] = useState('all'); // all | sigma | normal
  const [nexuFilterIds, setNexuFilterIds] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).get('nexu_filter')) {
        return JSON.parse(sessionStorage.getItem('nexu_filter_ids') || '[]');
      }
    } catch {}
    return null;
  });
  const [integrationFilter, setIntegrationFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('integration') || ''; } catch { return ''; }
  });
  const [importOpen, setImportOpen] = useState(false);
  const [importData, setImportData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteAlsoPanel, setDeleteAlsoPanel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Modal states
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [plansView, setPlansView] = useState('grupos'); // grupos | integracao | novo
  const [plansGrupoSel, setPlansGrupoSel] = useState(null); // {id, nome, provider} ou {id:'none'} ou {id:'sigma:xxx'}
  const [dragIdx, setDragIdx] = useState(null);
  const [editOrderIdx, setEditOrderIdx] = useState(null);
  const [ordemDrag, setOrdemDrag] = useState(null); // lista reordenada em tempo real durante o arraste
  const scrollBoxRef = useRef(null);
  const autoScrollRef = useRef(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [editingMsg, setEditingMsg] = useState(null); // { key, label, value }
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [renewingCustomer, setRenewingCustomer] = useState(null);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  
  // Busca Sigma para criação de cliente
  const [sigmaSearching, setSigmaSearching] = useState(false);
  const [sigmaSearchResults, setSigmaSearchResults] = useState([]);
  const [sigmaIntegration, setSigmaIntegration] = useState(null);
  
  // Form states
  const [customerForm, setCustomerForm] = useState({
    name: '',
    customer_name: '',
    phone: '',
    plan_id: '',
    connection_id: '',
    expiration_date: '',
    expiration_time: '09:00',
    send_7_days: true,
    send_5_days: true,
    send_4_days: false,
    send_3_days: true,
    send_2_days: false,
    send_1_day: true,
    send_on_day: true,
    send_1_day_after: true,
    notes: '',
    payment_link_enabled: true,
    custom_price: '',
    birthday: ''
  });
  
  const [planForm, setPlanForm] = useState({
    name: '',
    price: '',
    description: '',
    duration_months: 1,
    custo_mensal: '',
    active: true,
    sigma_package_id: '',
    sigma_login_integration_id: '',
    sigma_login_package_id: '',
    sigma_login_connections: 1,
  });
  const [panelIntegrations, setPanelIntegrations] = useState([]);
  const [panelPackages, setPanelPackages] = useState({}); // integId -> {loading, packages, note}
  const [deletePlanTarget, setDeletePlanTarget] = useState(null); // { plan, customers, loading }
  const [hasIntegrations, setHasIntegrations] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [syncingPhoneId, setSyncingPhoneId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { title, description, confirmLabel, danger, onConfirm }
  const [cobrancaModal, setCobrancaModal] = useState(null); // { customer, mensagens, msgKey, texto, loading, enviando }
  const [askOnboarding, setAskOnboarding] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [bulkAction, setBulkAction] = useState(null); // { type, label }
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkDelay, setBulkDelay] = useState({ min: 3, max: 6 });
  const [bulkDeletePanel, setBulkDeletePanel] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  
  const [settings, setSettings] = useState({});
  const [renewNotify, setRenewNotify] = useState(true);
  const [renewAccounts, setRenewAccounts] = useState([]);
  const [renewAccSel, setRenewAccSel] = useState({});
  const [renewForm, setRenewForm] = useState({
    type: 'months',
    months: 1,
    new_date: '',
    plan_id: ''  // Para renovação por plano (Sigma)
  });
  const [sigmaCreditsWarning, setSigmaCreditsWarning] = useState(null);
  const [checkingCredits, setCheckingCredits] = useState(false);
  
  const [campaignForm, setCampaignForm] = useState({
    plan_ids: [],  // Múltiplos planos
    mode: 'new', // 'new' ou 'existing'
    message: '',
    campaign_id: '',
    delay_seconds: 5,
    status_filter: 'all',  // all, active, expiring, expired
    target_mode: 'plan',   // 'plan' (por plano/status) ou 'selected' (clientes escolhidos)
    selected_ids: [],
    imageUrl: null,  // URL da imagem selecionada
    imageId: null    // ID da imagem selecionada
  });
  
  // Estado para acompanhamento de campanha em execução
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [campaignProgress, setCampaignProgress] = useState(null);
  const campaignPollingRef = useRef(null);
  
  const [saving, setSaving] = useState(false);
  const [enhancingText, setEnhancingText] = useState(false);
  const [deleteExpiredModalOpen, setDeleteExpiredModalOpen] = useState(false);
  const [sigmaPlansOpen, setSigmaPlansOpen] = useState(false);
  const [dominusImport, setDominusImport] = useState({ open: false, integ: null, loading: false, packages: [], precos: {}, saving: false });
  const [deletingExpired, setDeletingExpired] = useState(false);
  const [expiredSummary, setExpiredSummary] = useState({ total: 0, panel: 0 });
  const [deleteExpiredAlsoPanel, setDeleteExpiredAlsoPanel] = useState(false);

  const openDeleteExpired = () => {
    setDeleteExpiredAlsoPanel(false);
    setExpiredSummary({ total: expiredCustomers, panel: 0 });
    setDeleteExpiredModalOpen(true);
    api.get('/customers/expired/summary').then((r) => setExpiredSummary(r.data)).catch(() => {});
  };

  // Função para deletar clientes vencidos em massa
  const deleteExpiredCustomers = async () => {
    try {
      setDeletingExpired(true);
      const token = localStorage.getItem('nexus-token');
      const qs = deleteExpiredAlsoPanel ? '?also_panel=true' : '';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/expired/bulk${qs}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        (data.panel_failed || []).slice(0, 4).forEach((m) => toast.warning(m));
        setDeleteExpiredModalOpen(false);
        fetchCustomers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao deletar clientes vencidos');
      }
    } catch (error) {
      toast.error('Erro ao deletar clientes vencidos');
    } finally {
      setDeletingExpired(false);
    }
  };

  // Função para melhorar texto com IA
  const enhanceTextWithAI = async () => {
    if (!campaignForm.message.trim()) {
      toast.error('Digite uma mensagem primeiro');
      return;
    }
    
    setEnhancingText(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/enhance-text`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: campaignForm.message })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCampaignForm({ ...campaignForm, message: data.enhanced });
        toast.success('Texto melhorado com IA! ✨');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao melhorar texto');
      }
    } catch (error) {
      toast.error('Erro ao conectar com IA');
    } finally {
      setEnhancingText(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTemplates();
  }, [statusFilter]);

  // Atualização em TEMPO REAL: quando a IA (NEXU) faz uma ação, recarrega o gestor sem F5
  useEffect(() => {
    const onChanged = () => { fetchCustomers(); };
    window.addEventListener('nexu-data-changed', onChanged);
    // entre abas diferentes: o NEXU grava um timestamp no localStorage
    const onStorage = (e) => { if (e.key === 'nexu_data_changed') fetchCustomers(); };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('nexu-data-changed', onChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  
  // Carregar templates (apenas do usuário atual)
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates?owner_filter=mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };
  
  // Aplicar template na campanha
  const applyTemplateToMessage = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setCampaignForm({ ...campaignForm, message: template.message });
      toast.success(`Template "${template.name}" aplicado!`);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Buscar apenas customers e plans primeiro (mais rápido)
      const [customersRes, plansRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers?status=${statusFilter}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-plans`, { headers })
      ]);
      
      if (customersRes.ok) setCustomers(await customersRes.json());
      if (plansRes.ok) setPlans(await plansRes.json());
      
      // Buscar conexões e campanhas em segundo plano
      const [connectionsRes, campaignsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/quick?owner_filter=mine`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns`, { headers })
      ]);
      
      if (connectionsRes.ok) {
        const connectionsData = await connectionsRes.json();
        setConnections(connectionsData);
        
        // Auto-selecionar se tiver apenas 1 conexão
        if (connectionsData.length === 1) {
          setCustomerForm(prev => ({ ...prev, connection_id: connectionsData[0].id }));
        }
      }
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
      
      // Integração Sigma antiga (por token) — pode não existir mais; ignora 404 silenciosamente
      const sigmaRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sigma-integration`, { headers }).catch(() => ({ ok: false }));
      if (sigmaRes.ok) {
        const sigmaData = await sigmaRes.json();
        if (sigmaData?.enabled && sigmaData?.panel_url && sigmaData?.api_key) {
          setSigmaIntegration(sigmaData);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        // usa o delay configurado no gestor como padrão da cobrança em massa
        if (data.billing_delay_seconds != null && data.billing_delay_seconds > 0) {
          setBulkDelay({ min: data.billing_delay_seconds, max: data.billing_delay_seconds });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  // Buscar cliente no Sigma pelo username
  const searchSigmaClient = async (username) => {
    if (!username.trim() || !sigmaIntegration) return;
    
    setSigmaSearching(true);
    setSigmaSearchResults([]);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sigma/search-client?username=${encodeURIComponent(username)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          setSigmaSearchResults([data.client]);
        } else {
          toast.error('Cliente não encontrado no Sigma');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao buscar no Sigma');
      }
    } catch (error) {
      console.error('Erro ao buscar no Sigma:', error);
      toast.error('Erro ao buscar no Sigma');
    } finally {
      setSigmaSearching(false);
    }
  };

  // Aplicar cliente do Sigma no formulário
  const applySigmaClient = (client) => {
    // Pegar o primeiro plano Sigma disponível como padrão
    const firstSigmaPlan = plans.find(p => p.sigma_package_id);
    
    setCustomerForm({
      ...customerForm,
      name: client.name || client.username || '',
      phone: client.phone || '',
      plan_id: firstSigmaPlan?.id || '', // Primeiro plano Sigma da lista
      expiration_date: client.expiration_date?.split('T')[0] || '',
      sigma_username: client.username,
      sigma_password: client.password
    });
    setSigmaSearchResults([]);
    toast.success(`Cliente "${client.username}" carregado! Selecione o plano desejado.`);
  };

  const openCustomerModal = (customer = null) => {
    setImportData(null);
    if (customer) {
      setEditingCustomer(customer);
      setCustomerForm({
        name: customer.name,
        customer_name: customer.customer_name || '',
        sigma_username: customer.sigma_username,
        panel_customer_id: customer.panel_customer_id,
        sigma_login_integration_id: customer.sigma_login_integration_id,
        phone: customer.phone,
        plan_id: customer.plan_id,
        connection_id: customer.connection_id,
        expiration_date: customer.expiration_date.split('T')[0],
        expiration_time: customer.expiration_time || '09:00',
        send_7_days: customer.send_7_days,
        send_5_days: customer.send_5_days,
        send_4_days: customer.send_4_days,
        send_3_days: customer.send_3_days,
        send_2_days: customer.send_2_days,
        send_1_day: customer.send_1_day,
        send_on_day: customer.send_on_day,
        send_1_day_after: customer.send_1_day_after,
        notes: customer.notes || '',
        payment_link_enabled: customer.payment_link_enabled ?? true,
        custom_price: customer.custom_price ?? '',
        birthday: customer.birthday || ''
      });
    } else {
      setEditingCustomer(null);
      // Carregar configurações padrão
      fetchSettings().then(() => {
        setCustomerForm(prev => ({
          ...prev,
          name: '',
          customer_name: '',
          phone: '',
          plan_id: plans[0]?.id || '',
          connection_id: connections.find(c => c.status === 'connected')?.id || '',
          expiration_date: '',
          expiration_time: settings.default_expiration_time || '09:00',
          send_7_days: settings.default_send_7_days ?? true,
          send_5_days: settings.default_send_5_days ?? true,
          send_4_days: settings.default_send_4_days ?? false,
          send_3_days: settings.default_send_3_days ?? true,
          send_2_days: settings.default_send_2_days ?? false,
          send_1_day: settings.default_send_1_day ?? true,
          send_on_day: settings.default_send_on_day ?? true,
          send_1_day_after: settings.default_send_1_day_after ?? true,
          notes: ''
        }));
      });
    }
    setCustomerModalOpen(true);
  };

  const isSigmaCustomer = (c) => !!(c?.sigma_username || c?.sigma_login_integration_id || c?.import_source === 'sigma_login');
  const isSigmaPlan = (p) => !!(p?.sigma_package_id || p?.sigma_login_package_id);

  const isTestPlanName = (name) => /teste|test|trial|demo|gr[aá]tis|free|\d+\s*\/?\s*(h|hr|hrs|hora|horas|hour|hours)\b/i.test(name || '');
  // Planos que podem ser escolhidos na renovação deste cliente (mesma regra do link de pagamento e do app)
  const renewablePlansFor = (customer) => {
    if (!customer) return [];
    const current = plans.find((p) => p.id === customer.plan_id);
    let list = [];
    if (customer.custom_price === null || customer.custom_price === undefined) {
      if (customer.sigma_login_integration_id) {
        list = plans.filter((p) => p.active !== false
          && p.sigma_login_integration_id === customer.sigma_login_integration_id && !isTestPlanName(p.name));
      } else if (customer.sigma_username) {
        list = plans.filter((p) => p.active !== false && p.sigma_package_id);
      }
    }
    if (current && !list.some((p) => p.id === current.id)) list = [current, ...list];
    return list;
  };

  const applyImportedCustomer = ({ name, phone, plan, expiration_date, import_data }) => {
    if (plan) setPlans((prev) => (prev.some((x) => x.id === plan.id) ? prev : [...prev, plan]));
    setCustomerForm((prev) => ({
      ...prev,
      name: name || prev.name,
      phone: phone || prev.phone,
      plan_id: plan?.id || prev.plan_id,
      expiration_date: expiration_date || prev.expiration_date,
      // vincula à integração para o dropdown de plano mostrar os planos certos
      sigma_login_integration_id: import_data?.integration_id || prev.sigma_login_integration_id,
      sigma_username: import_data?.username || prev.sigma_username,
      panel_customer_id: import_data?.panel_customer_id || prev.panel_customer_id,
    }));
    setImportData(import_data);
    const faltando = [!phone && 'telefone'].filter(Boolean);
    toast.success(`Cliente "${import_data.username}" importado!${faltando.length ? ' Preencha o ' + faltando.join(', ') + '.' : ''}`);
  };

  const saveCustomer = async (sendOnboarding = undefined) => {
    const missing = [];
    // é cliente de integração?
    const ehIntegracao = !!(customerForm.sigma_login_integration_id || customerForm.sigma_username || customerForm.panel_customer_id);
    if (ehIntegracao) {
      // integração: usuário/login obrigatório, nome opcional
      if (!customerForm.name) missing.push('Usuário / Login');
    } else {
      // normal: nome do cliente obrigatório, usuário opcional
      if (!customerForm.customer_name && !customerForm.name) missing.push('Nome do cliente');
      // se não tem usuário/login, usa o nome do cliente como login também
      if (!customerForm.name && customerForm.customer_name) {
        customerForm.name = customerForm.customer_name;
      }
    }
    if (!customerForm.plan_id) missing.push('Plano');
    if (!customerForm.expiration_date) missing.push('Data de Vencimento');
    
    if (missing.length > 0) {
      toast.error(`Preencha: ${missing.join(', ')}`);
      return;
    }

    // Ao CRIAR (não editar), com telefone e conta com WhatsApp: pergunta se envia boas-vindas
    if (!editingCustomer && sendOnboarding === undefined && !isSimple && customerForm.phone) {
      setAskOnboarding(true);
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const url = editingCustomer 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/customers/${editingCustomer.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/customers`;
      
      // Preparar dados - converter custom_price para número ou null
      const formData = {
        ...customerForm,
        custom_price: customerForm.custom_price ? parseFloat(customerForm.custom_price) : null,
        send_onboarding: sendOnboarding === true,
      };
      if (!editingCustomer && importData) {
        formData.import_data = importData;
      }
      
      const res = await fetch(url, {
        method: editingCustomer ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(editingCustomer ? 'Cliente atualizado!' : 'Cliente cadastrado!');
        setCustomerModalOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar cliente');
      }
    } catch (error) {
      toast.error('Erro ao salvar cliente');
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = (customer) => {
    setDeleteAlsoPanel(false);
    setDeleteTarget(customer);
  };

  const confirmDeleteCustomer = async () => {
    const customer = deleteTarget;
    if (!customer) return;
    const linked = !!(customer.sigma_login_integration_id && customer.panel_customer_id);
    setDeleting(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const qs = linked && deleteAlsoPanel ? '?also_panel=true' : '';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customer.id}${qs}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(data.panel_message ? `Cliente excluído. ${data.panel_message}.` : 'Cliente excluído');
        setCustomers(prev => prev.filter(c => c.id !== customer.id));
        setDeleteTarget(null);
      } else {
        // falha no painel: nada foi excluído, o diálogo continua aberto para tentar de novo ou excluir só aqui
        toast.error(data.detail || 'Erro ao excluir cliente', { duration: 8000 });
      }
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    } finally {
      setDeleting(false);
    }
  };

  // agrupa os planos por integração (grupos que aparecem na tela inicial do modal)
  const gruposDePlanos = () => {
    const grupos = [];
    // uma opção por integração de painel (que tem planos OU permite criar)
    panelIntegrations.forEach((ig) => {
      const doPainel = plans.filter((p) => p.sigma_login_integration_id === ig.id);
      grupos.push({ key: ig.id, id: ig.id, nome: ig.name, provider: ig.provider, provider_label: ig.provider_label, count: doPainel.length });
    });
    // Sigma via token (planos antigos sem integração-login mas com sigma_package_id)
    const sigmaToken = plans.filter((p) => p.sigma_package_id && !p.sigma_login_integration_id);
    if (sigmaToken.length) grupos.push({ key: 'sigma-token', id: 'sigma-token', nome: 'Sigma (planos antigos)', provider: 'sigma', count: sigmaToken.length });
    // sem integração
    const semInteg = plans.filter((p) => !p.sigma_login_integration_id && !p.sigma_package_id);
    grupos.push({ key: 'none', id: 'none', nome: 'Sem integração', provider: null, count: semInteg.length });
    return grupos;
  };
  const planosDoGrupo = (grupo) => {
    if (!grupo) return [];
    if (grupo.id === 'none') return plans.filter((p) => !p.sigma_login_integration_id && !p.sigma_package_id);
    if (grupo.id === 'sigma-token') return plans.filter((p) => p.sigma_package_id && !p.sigma_login_integration_id);
    return plans.filter((p) => p.sigma_login_integration_id === grupo.id);
  };
  const salvarOrdem = async (lista) => {
    try { await api.put('/customer-plans/reorder', { ordered_ids: lista.map((p) => p.id) }); }
    catch { toast.error('Erro ao salvar ordem'); }
  };
  // aplica uma nova ordem (array de planos do grupo) no estado global + persiste
  const aplicarOrdem = (arr) => {
    const ids = new Set(arr.map((p) => p.id));
    setPlans((prev) => {
      const outros = prev.filter((p) => !ids.has(p.id));
      const reordenados = arr.map((p, i) => ({ ...p, sort_order: i }));
      return [...outros, ...reordenados];
    });
    salvarOrdem(arr);
  };
  const moverPlano = (lista, from, to) => {
    const arr = [...lista];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    aplicarOrdem(arr);
  };
  // ---- drag em tempo real: a lista se reorganiza enquanto arrasta ----
  const itemRefs = useRef({}); // id -> elemento DOM (para animar com FLIP)
  const dragIdRef = useRef(null); // id do plano sendo arrastado (estável durante o arraste)
  const posAntesRef = useRef(null); // posições dos itens ANTES do reorder (para o FLIP no layout effect)
  const onDragStartPlano = (lista, idx) => { dragIdRef.current = lista[idx].id; setDragIdx(idx); setOrdemDrag([...lista]); };
  const onDragEnterPlano = (idx) => {
    if (!dragIdRef.current) return;
    const cur = ordemDrag || [];
    const from = cur.findIndex((p) => p.id === dragIdRef.current);
    if (from === -1 || from === idx) return;
    // mede as posições ANTES de reordenar (guarda na ref; o useLayoutEffect anima depois)
    const pos = {};
    Object.entries(itemRefs.current).forEach(([id, el]) => { if (el) pos[id] = el.getBoundingClientRect().top; });
    posAntesRef.current = pos;
    const arr = [...cur];
    const [item] = arr.splice(from, 1);
    arr.splice(idx, 0, item);
    setOrdemDrag(arr);
    setDragIdx(idx);
  };
  const onDropPlano = () => {
    if (ordemDrag) aplicarOrdem(ordemDrag);
    // limpa transforms residuais do FLIP
    Object.values(itemRefs.current).forEach((el) => { if (el) { el.style.transition = ''; el.style.transform = ''; } });
    dragIdRef.current = null;
    setDragIdx(null); setOrdemDrag(null); pararAutoScroll();
  };
  // FLIP: após o React reordenar o DOM, anima cada item da posição antiga -> nova (deslize suave)
  useLayoutEffect(() => {
    const antes = posAntesRef.current;
    if (!antes) return;
    posAntesRef.current = null;
    Object.entries(itemRefs.current).forEach(([id, el]) => {
      if (!el || antes[id] === undefined) return;
      const depois = el.getBoundingClientRect().top;
      const delta = antes[id] - depois;
      if (Math.abs(delta) < 1) return;
      el.style.transition = 'none';
      el.style.transform = `translateY(${delta}px)`;
      // força reflow e então anima de volta ao lugar
      void el.offsetHeight;
      el.style.transition = 'transform .25s cubic-bezier(.2,.9,.25,1)';
      el.style.transform = '';
    });
  }, [ordemDrag]);

  // ---- autoscroll ao arrastar perto das bordas ----
  const pararAutoScroll = () => { if (autoScrollRef.current) { clearInterval(autoScrollRef.current); autoScrollRef.current = null; } };
  const onDragOverBox = (e) => {
    e.preventDefault();
    const box = scrollBoxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    const y = e.clientY;
    const zona = 40;
    let dir = 0;
    if (y < r.top + zona) dir = -1;
    else if (y > r.bottom - zona) dir = 1;
    if (dir === 0) { pararAutoScroll(); return; }
    if (!autoScrollRef.current) {
      autoScrollRef.current = setInterval(() => { box.scrollTop += dir * 12; }, 16);
    }
  };
  const openPlanModal = (plan = null) => {
    loadPanelIntegrations();
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name,
        price: plan.price.toString(),
        custo_mensal: plan.custo_mensal != null ? String(plan.custo_mensal) : '',
        description: plan.description || '',
        duration_months: plan.duration_months || 1,
        active: plan.active,
        sigma_package_id: plan.sigma_package_id || '',
        sigma_login_integration_id: plan.sigma_login_integration_id || '',
        sigma_login_package_id: plan.sigma_login_package_id || '',
        sigma_login_connections: plan.sigma_login_connections || 1,
      });
    } else {
      setEditingPlan(null);
      setPlanForm({ name: '', price: '', description: '', duration_months: 1, custo_mensal: '', active: true, sigma_package_id: '',
        sigma_login_integration_id: '', sigma_login_package_id: '', sigma_login_connections: 1 });
      setPlansView('grupos'); setPlansGrupoSel(null);
    }
    setPlanModalOpen(true);
  };

  useEffect(() => {
    api.get('/imports/sources').then((r) => setHasIntegrations((r.data.sources || []).length > 0)).catch(() => {});
  }, []);

  const loadPanelIntegrations = async () => {
    try {
      const r = await api.get('/panel-integrations');
      setPanelIntegrations(r.data.integrations || []);
    } catch { /* silencioso */ }
  };

  const savePlan = async () => {
    if (!planForm.name || !planForm.price) {
      toast.error('Preencha nome e valor do plano');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const url = editingPlan 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/customer-plans/${editingPlan.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/customer-plans`;
      
      const res = await fetch(url, {
        method: editingPlan ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...planForm,
          price: parseFloat(planForm.price),
          duration_months: (plansGrupoSel?.provider === 'gerenciaapp') ? 1 : (parseInt(planForm.duration_months) || 1),
          custo_mensal: planForm.custo_mensal !== '' ? parseFloat(planForm.custo_mensal) : null,
          sigma_login_integration_id: planForm.sigma_login_integration_id || null,
          sigma_login_package_id: planForm.sigma_login_package_id || null,
          sigma_login_connections: parseInt(planForm.sigma_login_connections) || 1,
        })
      });
      
      if (res.ok) {
        toast.success(editingPlan ? 'Plano atualizado!' : 'Plano criado!');
        // volta pra tela anterior (lista do grupo) em vez de fechar tudo
        setEditingPlan(null);
        setPlansView(plansGrupoSel ? 'integracao' : 'grupos');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar plano');
      }
    } catch (error) {
      toast.error('Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  const runDeletePlan = async (plan, force, deleteCustomers) => {
    const token = localStorage.getItem('nexus-token');
    const params = [];
    if (force) params.push('force=true');
    if (deleteCustomers) params.push('delete_customers=true');
    const qs = params.length ? `?${params.join('&')}` : '';
    return fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-plans/${plan.id}${qs}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
  };

  const deletePlan = async (plan) => {
    try {
      const res = await runDeletePlan(plan, false);
      if (res.ok) { toast.success('Plano excluído'); fetchData(); return; }
      if (res.status === 409) {
        // em uso: abre o modal com a lista de clientes afetados
        setDeletePlanTarget({ plan, customers: [], loading: true });
        try {
          const r = await api.get(`/customer-plans/${plan.id}/customers`);
          setDeletePlanTarget({ plan, customers: r.data.customers || [], loading: false });
        } catch {
          setDeletePlanTarget({ plan, customers: [], loading: false });
        }
        return;
      }
      const error = await res.json().catch(() => ({}));
      toast.error(error.detail || 'Erro ao excluir plano');
    } catch { toast.error('Erro ao excluir plano'); }
  };

  const confirmDeletePlanForce = async () => {
    if (!deletePlanTarget) return;
    const plan = deletePlanTarget.plan;
    const isIntegrationPlan = !!(plan.sigma_login_integration_id || plan.sigma_package_id);
    try {
      setDeletingPlan(true);
      const res = await runDeletePlan(plan, true, isIntegrationPlan);
      if (res.ok) {
        const d = await res.json().catch(() => ({}));
        if (isIntegrationPlan) {
          toast.success(`Plano excluído. ${d.deleted || 0} cliente(s) excluído(s).`);
        } else {
          toast.success(`Plano excluído. ${d.detached || 0} cliente(s) ficaram sem plano.`);
        }
        setDeletePlanTarget(null);
        fetchData();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Erro ao excluir plano');
      }
    } catch { toast.error('Erro ao excluir plano'); }
    finally { setDeletingPlan(false); }
  };

  const abrirModalCobranca = async (customer) => {
    setCobrancaModal({ customer, mensagens: [], msgKey: null, texto: '', loading: true, enviando: false });
    try {
      const r = await api.get(`/customers/${customer.id}/billing-messages`);
      const msgs = r.data.mensagens || [];
      const sugerida = msgs.find((m) => m.sugerida) || msgs[0];
      setCobrancaModal({
        customer, mensagens: msgs,
        msgKey: sugerida?.key, texto: sugerida?.texto || '',
        loading: false, enviando: false,
      });
    } catch (e) {
      toast.error('Erro ao carregar as mensagens');
      setCobrancaModal(null);
    }
  };

  const selecionarMsgCobranca = (key) => {
    setCobrancaModal((m) => {
      const msg = m.mensagens.find((x) => x.key === key);
      return { ...m, msgKey: key, texto: msg?.texto || '' };
    });
  };

  const enviarCobrancaModal = async () => {
    if (!cobrancaModal?.texto?.trim()) { toast.error('A mensagem está vazia'); return; }
    setCobrancaModal((m) => ({ ...m, enviando: true }));
    try {
      await api.post(`/customers/${cobrancaModal.customer.id}/send-billing`, {
        msg_key: cobrancaModal.msgKey,
        custom_message: cobrancaModal.texto,
      });
      toast.success(`Mensagem enviada para ${cobrancaModal.customer.name}!`);
      setCobrancaModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar');
      setCobrancaModal((m) => ({ ...m, enviando: false }));
    }
  };

  const sendBillingMessage = async (customer) => {
    try {
      const token = localStorage.getItem('nexus-token');
      toast.loading('Enviando cobrança...', { id: 'billing' });
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customer.id}/send-billing`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.dismiss('billing');
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Cobrança enviada para ${customer.name}!`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao enviar cobrança');
      }
    } catch (error) {
      toast.dismiss('billing');
      toast.error('Erro ao enviar cobrança');
    }
  };

  const sendPushNotification = async (customer) => {
    try {
      const token = localStorage.getItem('nexus-token');
      toast.loading('Enviando notificação...', { id: 'push' });
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customer.id}/send-push`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.dismiss('push');
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Notificação enviada: "${data.title}"`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao enviar notificação');
      }
    } catch (error) {
      toast.dismiss('push');
      toast.error('Erro ao enviar notificação');
    }
  };

  const uploadBillingImage = async (file) => {
    try {
      const token = localStorage.getItem('nexus-token');
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing-image/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (res.ok && data.url) { toast.success('Imagem enviada!'); return data.url; }
      toast.error(data.detail || 'Erro no upload'); return null;
    } catch { toast.error('Erro no upload'); return null; }
  };
  const testImagemKey = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/imagemapp/test-key`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: settings.imagemapp_api_key }) });
      const data = await res.json();
      if (data.ok) toast.success('Chave válida!'); else toast.error('Chave inválida: ' + (data.detalhe || ''));
    } catch { toast.error('Erro ao testar'); }
  };
  const openSettingsModal = async () => {
    await fetchSettings();
    setSettingsModalOpen(true);
  };

  const openCampaignModal = () => {
    setCampaignForm({
      plan_ids: [],
      mode: 'new',
      message: '',
      campaign_id: '',
      delay_seconds: 5,
      status_filter: 'all',
      image: null,
      imagePreview: null
    });
    setCampaignModalOpen(true);
  };

  const sendCampaign = async () => {
    if (campaignForm.mode === 'new' && !campaignForm.message.trim()) {
      toast.error('Digite uma mensagem para enviar');
      return;
    }
    if (campaignForm.mode === 'existing' && !campaignForm.campaign_id) {
      toast.error('Selecione uma campanha');
      return;
    }
    
    // Filter customers by plan and status
    let filteredCustomers = customers;
    
    // Filtrar por múltiplos planos
    if (campaignForm.target_mode === 'selected') {
      filteredCustomers = filteredCustomers.filter(c => campaignForm.selected_ids.includes(c.id));
    } else if (campaignForm.plan_ids && campaignForm.plan_ids.length > 0) {
      filteredCustomers = filteredCustomers.filter(c => campaignForm.plan_ids.includes(c.plan_id));
    }
    
    // Filter by status
    if (campaignForm.status_filter !== 'all') {
      filteredCustomers = filteredCustomers.filter(c => c.status === campaignForm.status_filter);
    }
    
    const targetCount = filteredCustomers.length;
    
    if (targetCount === 0) {
      toast.error('Nenhum cliente para enviar');
      return;
    }
    
    if (!confirm(`Enviar campanha para ${targetCount} cliente(s)?`)) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('nexus-token');
      
      const body = {
        plan_ids: (campaignForm.target_mode !== 'selected' && campaignForm.plan_ids && campaignForm.plan_ids.length > 0) ? campaignForm.plan_ids : null,
        customer_ids: (campaignForm.target_mode === 'selected' && campaignForm.selected_ids && campaignForm.selected_ids.length > 0) ? campaignForm.selected_ids : null,
        status_filter: (campaignForm.target_mode !== 'selected' && campaignForm.status_filter !== 'all') ? campaignForm.status_filter : null,
        delay_seconds: campaignForm.delay_seconds,
        image_url: campaignForm.imageUrl  // Já temos a URL da imagem selecionada
      };
      
      if (campaignForm.mode === 'new') {
        body.message = campaignForm.message;
      } else {
        body.campaign_id = campaignForm.campaign_id;
      }
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/campaign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setCampaignModalOpen(false);
        
        // Limpar imagem do form
        setCampaignForm(prev => ({ ...prev, imageUrl: null, imageId: null }));
        
        // Abrir acompanhamento da campanha
        setActiveCampaign({
          id: data.campaign_id,
          total: data.total_customers
        });
        startCampaignPolling(data.campaign_id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao enviar campanha');
      }
    } catch (error) {
      toast.error('Erro ao enviar campanha');
    } finally {
      setSaving(false);
    }
  };

  // Funções para acompanhamento de campanha
  const startCampaignPolling = (campaignId) => {
    if (campaignPollingRef.current) {
      clearInterval(campaignPollingRef.current);
    }
    
    // Buscar status imediatamente
    fetchCampaignStatus(campaignId);
    
    // Polling a cada 2 segundos
    campaignPollingRef.current = setInterval(() => {
      fetchCampaignStatus(campaignId);
    }, 2000);
  };
  
  const stopCampaignPolling = () => {
    if (campaignPollingRef.current) {
      clearInterval(campaignPollingRef.current);
      campaignPollingRef.current = null;
    }
  };
  
  const fetchCampaignStatus = async (campaignId) => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/campaign/${campaignId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCampaignProgress(data);
        
        // Parar polling se a campanha terminou
        if (['completed', 'stopped'].includes(data.status)) {
          stopCampaignPolling();
        }
      }
    } catch (error) {
      console.error('Erro ao buscar status da campanha:', error);
    }
  };
  
  const pauseCampaign = async () => {
    if (!activeCampaign?.id) return;
    try {
      const token = localStorage.getItem('nexus-token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/campaign/${activeCampaign.id}/pause`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campanha pausada');
    } catch (error) {
      toast.error('Erro ao pausar campanha');
    }
  };
  
  const resumeCampaign = async () => {
    if (!activeCampaign?.id) return;
    try {
      const token = localStorage.getItem('nexus-token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/campaign/${activeCampaign.id}/resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campanha retomada');
    } catch (error) {
      toast.error('Erro ao retomar campanha');
    }
  };
  
  const stopCampaign = async () => {
    if (!activeCampaign?.id) return;
    if (!confirm('Tem certeza que deseja parar a campanha? Os envios restantes serão cancelados.')) return;
    try {
      const token = localStorage.getItem('nexus-token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/campaign/${activeCampaign.id}/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campanha parada');
    } catch (error) {
      toast.error('Erro ao parar campanha');
    }
  };
  
  const closeCampaignProgress = () => {
    stopCampaignPolling();
    setActiveCampaign(null);
    setCampaignProgress(null);
  };

  const saveSettings = async (applyToAll = false) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...settings, apply_to_all: applyToAll })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Configurações salvas!');
        setSettingsModalOpen(false);
      } else {
        toast.error('Erro ao salvar configurações');
      }
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const syncPhone = async (customer) => {
    setSyncingPhoneId(customer.id);
    try {
      await api.post(`/customers/${customer.id}/sync-phone`);
    } catch (e) {
      // o POST só dispara o job; se ele demorar/timeout, o job segue rodando no servidor
      const d = e?.response?.data?.detail;
      if (typeof d === 'string') { toast.error(d); setSyncingPhoneId(null); return; }
    }
    toast.info('Sincronizando telefone no painel… aguarde alguns segundos.');
    // acompanha o resultado (job em segundo plano; 1º login do painel pode levar até ~1 min)
    for (let i = 0; i < 45; i++) {
      await new Promise((ok) => setTimeout(ok, 2000));
      try {
        const st = await api.get(`/customers/${customer.id}/sync-phone`);
        const status = st?.data?.status;
        if (status === 'done') { toast.success(st.data.message || 'Telefone sincronizado no painel'); setSyncingPhoneId(null); return; }
        if (status === 'error') { toast.error(st.data.message || 'Não consegui sincronizar o telefone'); setSyncingPhoneId(null); return; }
        // status === 'running' ou 404 (job ainda registrando): segue tentando
      } catch (e) {
        // 404 = job ainda não visível; qualquer outro erro de rede: segue tentando
      }
    }
    toast.warning('A sincronização está demorando. O número pode já ter sido atualizado no painel — confira em instantes.');
    setSyncingPhoneId(null);
  };

  const openRenewModal = (customer) => {
    setRenewingCustomer(customer);
    // Cliente Sigma: já deixa o plano atual selecionado (se for um plano do Sigma)
    const currentPlan = plans.find(p => p.id === customer?.plan_id);
    // cliente vinculado pelo login já sabe o pacote do painel, então o plano atual serve mesmo sem ser "plano Sigma"
    if (isSigmaCustomer(customer) && currentPlan) {
      setRenewForm({ type: 'plan', plan_id: currentPlan.id, months: 1, new_date: '' });
    } else {
      // cliente normal: já sugere a duração do plano atual (ex.: trimestral = 3 meses)
      setRenewForm({ type: 'months', months: currentPlan?.duration_months || 1, new_date: '' });
    }
    setSigmaCreditsWarning(null);
    setRenewNotify(true); setRenewAccounts([]); setRenewAccSel({});
    // multi-acessos: busca a lista para deixar escolher quais renovar
    if (customer.linked_accounts && customer.linked_accounts.length > 0) {
      api.get(`/customers/${customer.id}/accounts`).then((r) => {
        const accs = r.data.accounts || [];
        setRenewAccounts(accs);
        const sel = {}; accs.forEach((a) => { sel[a.account_id] = true; }); // todos por padrão
        setRenewAccSel(sel);
      }).catch(() => {});
    }
    setRenewModalOpen(true);
  };

  // Verificar créditos Sigma antes de renovar
  const checkSigmaCreditsAndRenew = async () => {
    // Se não for por meses ou não for cliente Sigma, renova direto
    if (renewForm.type !== 'months' || !isSigmaCustomer(renewingCustomer)) {
      renewCustomer();
      return;
    }
    
    setCheckingCredits(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sigma/check-credits`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: renewingCustomer.id,
          months: renewForm.months
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (!data.is_sigma_client) {
          // Não é cliente Sigma, renova normal
          renewCustomer();
        } else if (!data.integration_active) {
          // Integração desativada - mostra aviso
          setSigmaCreditsWarning({
            type: 'integration_inactive',
            message: 'Sua integração Sigma está desativada. O cliente será renovado apenas no Nexus.'
          });
        } else if (!data.has_credits) {
          // Sem créditos suficientes - mostra aviso
          setSigmaCreditsWarning({
            type: 'insufficient_credits',
            credits: data.credits,
            required: data.required,
            message: `Créditos insuficientes no Sigma (${data.credits} disponíveis, ${data.required} necessários). O cliente será renovado apenas no Nexus.`
          });
        } else {
          // Tudo OK, renova
          renewCustomer();
        }
      } else {
        // Erro na verificação, renova mesmo assim
        renewCustomer();
      }
    } catch (error) {
      // Erro na verificação, renova mesmo assim
      renewCustomer();
    } finally {
      setCheckingCredits(false);
    }
  };

  const renewCustomer = async () => {
    setSaving(true);
    setSigmaCreditsWarning(null);
    try {
      const token = localStorage.getItem('nexus-token');
      let body = {};
      
      if (renewForm.type === 'plan' && renewForm.plan_id) {
        body = { plan_id: renewForm.plan_id };
      } else if (renewForm.type === 'months') {
        body = { months: renewForm.months };
      } else {
        body = { new_date: renewForm.new_date };
      }
      
      if (renewAccounts.length > 0) {
        const chosen = renewAccounts.filter((a) => renewAccSel[a.account_id]).map((a) => a.account_id);
        if (chosen.length === 0) { toast.error('Selecione ao menos um acesso'); return; }
        if (chosen.length < renewAccounts.length) body.account_ids = chosen;
      }
      body.notify = renewNotify;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${renewingCustomer.id}/renew`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        const data = await res.json();
        // painel falhou → pergunta se quer renovar só no NexuZap
        if (data.painel_falhou) {
          const querAssimMesmo = window.confirm(`${data.sigma_error || 'A renovação no painel falhou.'}\n\nDeseja renovar APENAS no NexuZap? (o painel da integração NÃO será alterado)`);
          if (querAssimMesmo) {
            const res2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${renewingCustomer.id}/renew`, {
              method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...body, forcar_apenas_nexuzap: true }),
            });
            if (res2.ok) { toast.success('Renovado apenas no NexuZap.'); setRenewModalOpen(false); fetchData(); }
            else { toast.error('Erro ao renovar no NexuZap'); }
          }
          return;
        }
        toast.success('Cliente renovado!');
        if (data.sigma_error) {
          toast.error(`Não renovou no painel: ${data.sigma_error}`, { duration: 10000 });
        }
        setRenewModalOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao renovar');
      }
    } catch (error) {
      toast.error('Erro ao renovar');
    } finally {
      setSaving(false);
    }
  };

  const getRiskBadge = (risk) => {
    if (!risk || risk.level === 'low') return null;
    const cfg = risk.level === 'high'
      ? { cls: 'bg-red-500/15 text-red-400 border-red-500/30', label: 'Risco alto' }
      : { cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', label: 'Risco médio' };
    const tip = (risk.reasons || []).join(' · ') || 'Chance de não renovar';
    return (
      <Badge variant="outline" className={`text-xs ${cfg.cls}`} title={`${tip} (score ${risk.score})`}>
        ⚠ {cfg.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Ativo</Badge>;
      case 'expiring':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><AlertCircle className="w-3 h-3 mr-1" />Vencendo</Badge>;
      case 'expired':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Vencido</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return 'N/A';
      
      // Se já está no formato DD/MM/YYYY, retorna direto
      if (dateStr.includes('/') && dateStr.split('/').length === 3) {
        const parts = dateStr.split('/');
        // Verificar se é DD/MM/YYYY (dia tem 2 dígitos e é <= 31)
        if (parts[0].length <= 2 && parseInt(parts[0]) <= 31) {
          return dateStr.split(' ')[0]; // Remove hora se tiver
        }
      }
      
      // Se tem T (ISO format), pegar apenas a parte da data
      if (dateStr.includes('T')) {
        const parts = dateStr.split('T')[0].split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
      }
      
      // Se está no formato YYYY-MM-DD
      if (dateStr.includes('-') && dateStr.split('-').length === 3) {
        const parts = dateStr.split('-');
        if (parts[0].length === 4) { // Ano primeiro
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
      }
      
      // Fallback
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return dateStr; // Retorna original se não conseguir parsear
      }
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr || 'N/A';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para parsear data em qualquer formato
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    
    // Se está no formato DD/MM/YYYY
    if (dateStr.includes('/')) {
      const parts = dateStr.split(' ')[0].split('/'); // Remove hora se tiver
      if (parts.length === 3 && parts[0].length <= 2) {
        return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      }
    }
    
    // Se está no formato YYYY-MM-DD ou ISO
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? new Date(0) : date;
  };

  // Filtrar e ordenar clientes por data de expiração (mais próximo primeiro)
  const filteredCustomers = customers
    .filter(c => {
      if (nexuFilterIds && !nexuFilterIds.includes(c.id)) return false;
      if (integrationFilter && c.sigma_login_integration_id !== integrationFilter) return false;
      if (riskFilter && !(c.churn_risk && c.churn_risk.level !== 'low')) return false;
      if (originFilter === 'sigma' && !isSigmaCustomer(c)) return false;
      if (originFilter === 'normal' && isSigmaCustomer(c)) return false;
      if (originFilter.startsWith('integ:') && c.integration_name !== originFilter.slice(6)) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return c.name.toLowerCase().includes(search) || (c.customer_name || "").toLowerCase().includes(search) || c.phone.includes(search);
      }
      return true;
    })
    .sort((a, b) => {
      // Ordenar por data de expiração - mais próximo de expirar primeiro
      const dateA = parseDate(a.expiration_date);
      const dateB = parseDate(b.expiration_date);
      return dateA - dateB;
    });

  // Seleção em massa
  const selectedList = filteredCustomers.filter(c => selectedIds[c.id]);
  const selectedCount = selectedList.length;
  const toggleSelect = (id) => setSelectedIds(prev => ({ ...prev, [id]: !prev[id] }));
  const clearSelection = () => setSelectedIds({});
  const toggleSelectAllVisible = () => {
    const allSelected = paginatedCustomers.every(c => selectedIds[c.id]);
    const next = { ...selectedIds };
    paginatedCustomers.forEach(c => { next[c.id] = !allSelected; });
    setSelectedIds(next);
  };

  const runBulk = async () => {
    if (!bulkAction || selectedCount === 0) return;
    const ids = selectedList.map(c => c.id);

    // Cobrança e Push em massa: rodam no SERVIDOR (continuam mesmo fechando a tela)
    if (bulkAction.type === 'billing' || bulkAction.type === 'push') {
      setBulkRunning(true);
      try {
        await api.post('/customers/bulk-billing', {
          customer_ids: ids,
          delay_min: parseInt(bulkDelay.min) || 0,
          delay_max: parseInt(bulkDelay.max) || 0,
          kind: bulkAction.type,
        });
        toast.success('Envio iniciado! Acompanhe pela bolinha no canto da tela.');
        setBulkAction(null);
        clearSelection();
        window.dispatchEvent(new CustomEvent('bulk-billing-started'));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Erro ao iniciar envio');
      } finally { setBulkRunning(false); }
      return;
    }

    setBulkRunning(true);
    let ok = 0, fail = 0;
    try {
      for (const id of ids) {
        try {
          if (bulkAction.type === 'push') {
            await api.post(`/customers/${id}/send-push`);
          } else if (bulkAction.type === 'delete') {
            const cust = selectedList.find(c => c.id === id);
            const alsoPanel = bulkDeletePanel && cust?.sigma_login_integration_id && cust?.panel_customer_id;
            await api.delete(`/customers/${id}${alsoPanel ? '?also_panel=true' : ''}`);
          }
          ok++;
        } catch { fail++; }
      }
      toast.success(`${ok} concluído(s)${fail ? `, ${fail} falha(s)` : ''}`);
      setBulkAction(null);
      clearSelection();
      fetchData();
    } finally {
      setBulkRunning(false);
    }
  };

  // Paginação
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCustomers = filteredCustomers.slice(startIndex, startIndex + itemsPerPage);

  // Reset página quando filtro muda
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, originFilter, riskFilter]);

  // Stats
  const totalCustomers = customers.length;
  const riskCount = customers.filter(c => c.churn_risk && c.churn_risk.level !== 'low').length;
  const activeCustomers = customers.filter(c => c.status === 'active').length;
  const expiringCustomers = customers.filter(c => c.status === 'expiring').length;
  const expiredCustomers = customers.filter(c => c.status === 'expired').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Clientes</h1>
          <p className="text-muted-foreground text-sm">Gerencie seus clientes e cobranças</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isSimple && (
            <Button variant="outline" size="sm" onClick={openCampaignModal}>
              <Megaphone className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Campanha</span>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={openSettingsModal}>
            <Settings className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Configurações</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => openPlanModal()}>
            <DollarSign className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Planos</span>
          </Button>
          <Button size="sm" onClick={() => openCustomerModal()}>
            <Plus className="w-4 h-4 mr-1" />
            Novo Cliente
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="cursor-pointer hover:border-primary/50" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-lg md:text-2xl font-bold">{totalCustomers}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-green-500/50" onClick={() => setStatusFilter('active')}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-lg md:text-2xl font-bold">{activeCustomers}</p>
                <p className="text-xs text-muted-foreground">Ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-yellow-500/50" onClick={() => setStatusFilter('expiring')}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-lg md:text-2xl font-bold">{expiringCustomers}</p>
                <p className="text-xs text-muted-foreground">Vencendo</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-red-500/50" onClick={() => setStatusFilter('expired')}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-lg md:text-2xl font-bold">{expiredCustomers}</p>
                <p className="text-xs text-muted-foreground">Vencidos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de crescimento */}
      <CustomerGrowthChart api={api} />

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant={riskFilter ? 'default' : 'outline'}
          size="sm"
          className={riskFilter ? 'bg-red-500 hover:bg-red-600' : 'text-red-500 border-red-500/30 hover:bg-red-500/10'}
          onClick={() => setRiskFilter(v => !v)}
          title="Mostrar só clientes com risco de cancelamento"
        >
          ⚠ Em risco{riskCount > 0 ? ` (${riskCount})` : ''}
        </Button>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="expiring">Vencendo</SelectItem>
            <SelectItem value="expired">Vencidos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={originFilter} onValueChange={setOriginFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="origin-filter">
            <SelectValue placeholder="Origem" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as origens</SelectItem>
            <SelectItem value="sigma">Todas as integrações</SelectItem>
            {[...new Set(customers.map((c) => c.integration_name).filter(Boolean))].sort().map((nome) => (
              <SelectItem key={nome} value={`integ:${nome}`}>Integração: {nome}</SelectItem>
            ))}
            <SelectItem value="normal">Clientes normais</SelectItem>
          </SelectContent>
        </Select>
        {expiredCustomers > 0 && (
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => openDeleteExpired()}
            className="whitespace-nowrap"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Apagar Vencidos ({expiredCustomers})
          </Button>
        )}
      </div>
      <SigmaPlansManager open={sigmaPlansOpen} onClose={() => { setSigmaPlansOpen(false); fetchData(); }} />

      {/* Importar planos do Dominus (catálogo) */}
      <Dialog open={dominusImport.open} onOpenChange={(o) => { if (!o) setDominusImport((d) => ({ ...d, open: false })); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-primary" /> Importar planos do Dominus</DialogTitle>
            <DialogDescription>Defina o preço de venda de cada plano do painel. O nome e a duração vêm do Dominus.</DialogDescription>
          </DialogHeader>
          {dominusImport.loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : dominusImport.packages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Nenhum plano encontrado no catálogo do Dominus.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto py-1">
              {dominusImport.packages.map((p) => {
                const meses = p.duration_months || (p.duration_days ? Math.max(1, Math.round(p.duration_days/30)) : 1);
                return (
                  <div key={p.id} className="flex items-center gap-2 rounded-lg border border-border/50 p-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-[11px] text-muted-foreground">{p.connections} tela(s) · {meses} {meses === 1 ? 'mês' : 'meses'}{p.has_plan ? ' · já criado' : ''}</p>
                    </div>
                    {p.has_plan ? (
                      <span className="text-xs text-emerald-500 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> criado</span>
                    ) : (
                      <Input type="number" min="0" step="0.01" placeholder="Preço R$" className="w-28 h-8"
                        value={dominusImport.precos[p.id] ?? ''}
                        onChange={(e) => setDominusImport((d) => ({ ...d, precos: { ...d.precos, [p.id]: e.target.value } }))} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDominusImport((d) => ({ ...d, open: false }))}>Cancelar</Button>
            <Button disabled={dominusImport.saving} onClick={async () => {
              const pend = dominusImport.packages.filter((p) => !p.has_plan && (dominusImport.precos[p.id] ?? '') !== '');
              if (!pend.length) { toast.error('Preencha o preço de pelo menos um plano'); return; }
              setDominusImport((d) => ({ ...d, saving: true }));
              try {
                const r = await api.post(`/sigma/login/${dominusImport.integ.id}/create-plans`, {
                  packages: pend.map((p) => ({
                    package_id: p.id, package_name: p.name, plan_name: p.name,
                    connections: p.connections || 1,
                    price: parseFloat(dominusImport.precos[p.id]),
                    duration_months: p.duration_months || (p.duration_days ? Math.max(1, Math.round(p.duration_days/30)) : 1),
                  })),
                });
                toast.success(r.data.message || 'Planos importados!');
                setDominusImport((d) => ({ ...d, open: false, saving: false }));
                fetchData();
              } catch (e) {
                toast.error('Erro ao importar planos');
                setDominusImport((d) => ({ ...d, saving: false }));
              }
            }}>{dominusImport.saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Importar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer List */}
      {filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhum cliente encontrado</h3>
            <p className="text-muted-foreground text-sm mb-4">
              {searchTerm ? 'Tente outra busca' : 'Cadastre seu primeiro cliente'}
            </p>
            {!searchTerm && (
              <Button onClick={() => openCustomerModal()}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Cliente
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Barra de seleção / ações em massa */}
          <div className={`flex items-center gap-3 flex-wrap px-3 py-2 rounded-xl transition-colors ${selectedCount > 0 ? 'bg-primary/[0.06] border border-primary/20' : ''}`}>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={paginatedCustomers.length > 0 && paginatedCustomers.every(c => selectedIds[c.id])}
                onChange={toggleSelectAllVisible}
                className="w-[18px] h-[18px] rounded-md cursor-pointer appearance-none border border-border/70 bg-muted/30 checked:bg-primary checked:border-primary transition-colors relative before:content-['✓'] before:absolute before:inset-0 before:flex before:items-center before:justify-center before:text-[11px] before:text-primary-foreground before:opacity-0 checked:before:opacity-100"
              />
              Selecionar todos
            </label>
            {selectedCount > 0 && (
              <>
                <span className="text-sm text-muted-foreground">{selectedCount} selecionado(s)</span>
                <div className="flex items-center gap-2 ml-auto flex-wrap">
                  {!isSimple && (
                    <Button size="sm" variant="outline" className="text-blue-500 border-blue-500/30 hover:bg-blue-500/10"
                      onClick={() => setBulkAction({ type: 'billing', label: 'Enviar cobrança' })}>
                      <Send className="w-4 h-4 mr-1.5" /> Cobrar
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-purple-500 border-purple-500/30 hover:bg-purple-500/10"
                    onClick={() => setBulkAction({ type: 'push', label: 'Notificar no app' })}>
                    <Bell className="w-4 h-4 mr-1.5" /> Notificar
                  </Button>
                  {!isSimple && (
                    <Button size="sm" variant="outline" className="text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10"
                      onClick={() => { setCampaignForm(f => ({ ...f, target_mode: 'selected', selected_ids: selectedList.map(c => c.id) })); setCampaignModalOpen(true); }}>
                      <Send className="w-4 h-4 mr-1.5" /> Campanha
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                    onClick={() => { setBulkDeletePanel(false); setBulkAction({ type: 'delete', label: 'Excluir' }); }}>
                    <Trash2 className="w-4 h-4 mr-1.5" /> Excluir
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSelection}>Limpar</Button>
                </div>
              </>
            )}
          </div>

          <div className="grid gap-3">
            {paginatedCustomers.map(customer => (
              <Card key={customer.id} className={`transition-all ${selectedIds[customer.id] ? 'border-primary/60 bg-primary/[0.06] shadow-[0_0_0_1px_hsl(var(--primary)/0.3)]' : 'hover:border-primary/30'}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={!!selectedIds[customer.id]}
                        onChange={() => toggleSelect(customer.id)}
                        className="mt-1.5 w-[18px] h-[18px] rounded-md cursor-pointer shrink-0 appearance-none border border-border/70 bg-muted/30 checked:bg-primary checked:border-primary transition-colors relative before:content-['✓'] before:absolute before:inset-0 before:flex before:items-center before:justify-center before:text-[11px] before:text-primary-foreground before:opacity-0 checked:before:opacity-100"
                        title="Selecionar"
                      />
                      <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{customer.customer_name || customer.name}</h3>
                        {customer.customer_name && customer.customer_name !== customer.name && (
                          <span className="text-xs text-muted-foreground font-mono truncate" title="Usuário no painel">({customer.name})</span>
                        )}
                        {customer.birthday_gift_status === 'available' && (
                          <span title="Presente de aniversário disponível" className="text-pink-500 animate-bounce" style={{ animationDuration: '2s' }}>🎁</span>
                        )}
                        {customer.birthday_gift_status === 'claimed' && (
                          <span title="Presente de aniversário resgatado">🎊</span>
                        )}
                        {getStatusBadge(customer.status)}
                        {getRiskBadge(customer.churn_risk)}
                        {isSigmaCustomer(customer) && (
                          <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30" title="Integração deste cliente">
                            {customer.integration_name || 'Sigma'}
                          </Badge>
                        )}
                        {customer.sigma_renew_pending && (
                          <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30"
                            title={`Renovado no NexuZap, mas não no Sigma: ${customer.sigma_renew_pending.error || ''}${customer.sigma_renew_pending.retryable ? ' — nova tentativa automática em andamento' : ' — renove manualmente no painel ou pelo botão Renovar'}`}>
                            {customer.sigma_renew_pending.retryable ? 'Sigma: tentando renovar' : 'Sigma não renovado'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {customer.phone}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {customer.plan_name} - {formatCurrency(customer.plan_price || 0)}
                          {customer.custom_price !== null && customer.custom_price !== undefined && (
                            <Badge variant="outline" className="ml-1 text-xs py-0 px-1 text-orange-500 border-orange-500/50">
                              Personalizado
                            </Badge>
                          )}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Vence: {formatDate(customer.expiration_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {customer.expiration_time}
                        </span>
                      </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 flex-shrink-0 justify-end max-w-[340px]">
                      {customer.payment_link_enabled && (
                        <ActionBtn icon={Link2} label="Copiar link" color="purple"
                          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/pagamento/${customer.id}`); toast.success('Link de pagamento copiado!'); }} />
                      )}
                      {!isSimple && (
                        <ActionBtn icon={Send} label="Cobrar no WhatsApp" color="blue"
                          onClick={() => abrirModalCobranca(customer)} />
                      )}
                      <ActionBtn icon={Bell} label="Notificar no app" color="purple"
                        onClick={() => setConfirmAction({
                          title: 'Enviar notificação?', description: `Enviar uma notificação push no app para ${customer.name}?`,
                          confirmLabel: 'Enviar', onConfirm: () => sendPushNotification(customer) })} />
                      {hasIntegrations && !isSigmaCustomer(customer) && (
                        <ActionBtn icon={Link2} label="Vincular a painel" color="sky"
                          onClick={() => setLinkTarget(customer)} />
                      )}
                      {customer.sigma_login_integration_id && customer.panel_customer_id && (
                        <ActionBtn icon={Phone} label="Sincronizar telefone" color="sky"
                          loading={syncingPhoneId === customer.id}
                          onClick={() => setConfirmAction({
                            title: 'Sincronizar telefone?', description: `Salvar o telefone de ${customer.name} no painel? O número do NexuZap tem prioridade.`,
                            confirmLabel: 'Sincronizar', onConfirm: () => syncPhone(customer) })} />
                      )}
                      <ActionBtn icon={RefreshCw} label="Renovar" color="green"
                        onClick={() => openRenewModal(customer)} />
                      <ActionBtn icon={Edit} label="Editar" color="default"
                        onClick={() => openCustomerModal(customer)} />
                      <ActionBtn icon={Trash2} label="Excluir" color="red"
                        onClick={() => deleteCustomer(customer)} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t relative">
              <div className="flex items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredCustomers.length)} de {filteredCustomers.length}
                </p>
                <select
                  value={itemsPerPage}
                  onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                >
                  <option value={10}>10/pag</option>
                  <option value={50}>50/pag</option>
                  <option value={100}>100/pag</option>
                </select>
              </div>
              <div className="flex items-center gap-2 sm:absolute sm:left-1/2 sm:-translate-x-1/2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm px-2">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <ImportCustomerDialog open={importOpen} onOpenChange={setImportOpen} api={api} onImported={applyImportedCustomer} />

      <LinkCustomerDialog open={!!linkTarget} onOpenChange={(o) => { if (!o) setLinkTarget(null); }}
        api={api} customer={linkTarget} onLinked={() => { setLinkTarget(null); fetchData(); }} />

      {/* Confirmação de ação em massa */}
      <Dialog open={!!bulkAction} onOpenChange={(o) => { if (!o && !bulkRunning) setBulkAction(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{bulkAction?.label} — {selectedCount} cliente(s)</DialogTitle>
            <DialogDescription>
              {bulkAction?.type === 'delete'
                ? `Excluir ${selectedCount} cliente(s)?`
                : (bulkAction?.type === 'billing' || bulkAction?.type === 'push')
                ? `${bulkAction?.type === 'push' ? 'Enviar notificação' : 'Enviar cobrança'} para ${selectedCount} cliente(s). Roda em segundo plano — pode fechar esta tela.`
                : `Confirmar "${bulkAction?.label}" para ${selectedCount} cliente(s) selecionado(s)?`}
            </DialogDescription>
          </DialogHeader>
          {bulkAction?.type === 'delete' && (() => {
            const fromPanel = selectedList.filter(c => c.sigma_login_integration_id && c.panel_customer_id && !['dominus','natv'].includes(c.integration_provider || provOf(c.sigma_login_integration_id))).length;
            return (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Eles podem ser recuperados por 30 dias.</p>
                {fromPanel > 0 && (
                  <label className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-2.5 cursor-pointer">
                    <input type="checkbox" checked={bulkDeletePanel}
                      onChange={(e) => setBulkDeletePanel(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-red-500" />
                    <span className="text-xs">
                      Excluir também no painel da integração
                      <span className="text-muted-foreground"> ({fromPanel} cliente(s) de integração). Se falhar no painel, o cliente não é excluído aqui.</span>
                    </span>
                  </label>
                )}
              </div>
            );
          })()}
          {(bulkAction?.type === 'billing' || bulkAction?.type === 'push') && (
            <div className="space-y-2">
              <Label className="text-xs">Intervalo entre mensagens (segundos)</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Input type="number" min="0" value={bulkDelay.min}
                    onChange={(e) => setBulkDelay({ ...bulkDelay, min: e.target.value })} placeholder="mín" />
                  <p className="text-[10px] text-muted-foreground mt-0.5 text-center">mínimo</p>
                </div>
                <span className="text-muted-foreground">até</span>
                <div className="flex-1">
                  <Input type="number" min="0" value={bulkDelay.max}
                    onChange={(e) => setBulkDelay({ ...bulkDelay, max: e.target.value })} placeholder="máx" />
                  <p className="text-[10px] text-muted-foreground mt-0.5 text-center">máximo</p>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">Um intervalo aleatório entre esses valores é aplicado entre cada envio (evita bloqueio do WhatsApp).</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkAction(null)} disabled={bulkRunning}>Cancelar</Button>
            <Button variant={bulkAction?.type === 'delete' ? 'destructive' : 'default'} onClick={runBulk} disabled={bulkRunning}>
              {bulkRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de cobrança: escolher qual mensagem + editar antes de enviar */}
      <Dialog open={!!cobrancaModal} onOpenChange={(o) => { if (!o) setCobrancaModal(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-primary" /> Enviar mensagem</DialogTitle>
            <DialogDescription>
              {cobrancaModal?.customer?.name} · escolha a mensagem, edite se quiser e envie.
            </DialogDescription>
          </DialogHeader>
          {cobrancaModal?.loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : (
            <div className="space-y-3 py-1">
              <div>
                <label className="text-xs text-muted-foreground">Tipo de mensagem</label>
                <Select value={cobrancaModal?.msgKey || ''} onValueChange={selecionarMsgCobranca}>
                  <SelectTrigger><SelectValue placeholder="Escolha a mensagem" /></SelectTrigger>
                  <SelectContent>
                    {(cobrancaModal?.mensagens || []).map((m) => (
                      <SelectItem key={m.key} value={m.key}>
                        {m.label}{m.sugerida ? ' ⭐ (sugerida)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Mensagem (edite à vontade)</label>
                <Textarea rows={7} value={cobrancaModal?.texto || ''}
                  onChange={(e) => setCobrancaModal((m) => ({ ...m, texto: e.target.value }))}
                  className="resize-none text-sm" />
                <p className="text-[11px] text-muted-foreground mt-1">As variáveis já foram trocadas pelos dados do cliente. Você pode editar o texto acima.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCobrancaModal(null)}>Cancelar</Button>
            <Button onClick={enviarCobrancaModal} disabled={cobrancaModal?.enviando || cobrancaModal?.loading}>
              {cobrancaModal?.enviando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4 mr-2" /> Enviar no WhatsApp</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor de uma mensagem específica (janela flutuante) */}
      <Dialog open={!!editingMsg} onOpenChange={(o) => { if (!o) setEditingMsg(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingMsg?.label}</DialogTitle>
            <DialogDescription>Edite a mensagem. Use as variáveis abaixo — elas são trocadas pelos dados do cliente no envio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {['{nome}', '{plano}', '{valor}', '{vencimento}', '{dias_restantes}', '{Link_de_pagamento}', ...(editingMsg?.key === 'msg_renewal' ? ['{comprovante}'] : []), ...(settings.fidelidade_enabled ? ['{selos}', '{selos_meta}', '{selos_faltam}', '{recompensa}'] : [])].map((v) => (
                <button key={v} type="button"
                  onClick={() => setEditingMsg((m) => ({ ...m, value: (m.value || '') + v }))}
                  className="text-[11px] font-mono px-2 py-1 rounded-md bg-muted hover:bg-primary/15 hover:text-primary transition-colors"
                  title="Clique para inserir">
                  {v}
                </button>
              ))}
            </div>
            <Textarea
              rows={8}
              autoFocus
              value={editingMsg?.value || ''}
              onChange={(e) => setEditingMsg((m) => ({ ...m, value: e.target.value }))}
              placeholder="Digite a mensagem..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMsg(null)}>Cancelar</Button>
            <Button onClick={() => {
              setSettings((prev) => ({ ...prev, [editingMsg.key]: editingMsg.value }));
              setEditingMsg(null);
              toast.success('Mensagem atualizada — não esqueça de salvar as configurações.');
            }}>
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enviar boas-vindas ao criar cliente */}
      <Dialog open={askOnboarding} onOpenChange={(o) => { if (!o) setAskOnboarding(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enviar boas-vindas?</DialogTitle>
            <DialogDescription>
              Quer enviar uma mensagem de boas-vindas no WhatsApp para <b>{customerForm.name}</b> agora?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setAskOnboarding(false); saveCustomer(false); }}>
              Não, só cadastrar
            </Button>
            <Button onClick={() => { setAskOnboarding(false); saveCustomer(true); }}>
              Sim, enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação genérica de ações do cliente */}
      <Dialog open={!!confirmAction} onOpenChange={(o) => { if (!o) setConfirmAction(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{confirmAction?.title}</DialogTitle>
            <DialogDescription>{confirmAction?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancelar</Button>
            <Button variant={confirmAction?.danger ? 'destructive' : 'default'}
              onClick={() => { const a = confirmAction; setConfirmAction(null); a?.onConfirm?.(); }}>
              {confirmAction?.confirmLabel || 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excluir cliente (com opção de excluir também no painel da integração) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir {deleteTarget?.name}?</DialogTitle>
            <DialogDescription>
              O cliente sai do NexuZap e fica 30 dias disponível para contato com o suporte.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget?.sigma_login_integration_id && deleteTarget?.panel_customer_id && !['dominus','natv'].includes(deleteTarget.integration_provider || provOf(deleteTarget.sigma_login_integration_id)) && (
            <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${deleteAlsoPanel ? 'border-red-500/40 bg-red-500/5' : 'border-border/60'}`}>
              <Switch checked={deleteAlsoPanel} onCheckedChange={setDeleteAlsoPanel} className="mt-0.5" data-testid="delete-also-panel" />
              <span className="text-sm">
                Excluir também em <b>{deleteTarget.integration_name || 'Sigma'}</b>
                {deleteTarget.sigma_username && <span className="text-muted-foreground"> (usuário {deleteTarget.sigma_username})</span>}
                <span className="block text-xs text-muted-foreground mt-1">
                  No painel a exclusão é definitiva e o cliente perde o acesso. Se o painel não responder, nada é excluído.
                </span>
              </span>
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDeleteCustomer} disabled={deleting} data-testid="confirm-delete-customer">
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {deleteAlsoPanel ? 'Excluir nos dois' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Modal */}
      <Dialog open={customerModalOpen} onOpenChange={setCustomerModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
          </DialogHeader>
          
          {/* Busca Sigma - só aparece na criação e se integração estiver ativa */}
          {!editingCustomer && sigmaIntegration && (
            <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">Buscar Cliente no Sigma</span>
                <Badge variant="outline" className="text-xs">Integração Ativa</Badge>
              </div>
              
              <div className="flex gap-2">
                <Input
                  placeholder="Digite o username do Sigma..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      searchSigmaClient(e.target.value);
                    }
                  }}
                />
                <Button 
                  variant="outline" 
                  onClick={(e) => {
                    const input = e.target.closest('.flex').querySelector('input');
                    if (input) searchSigmaClient(input.value);
                  }}
                  disabled={sigmaSearching}
                >
                  {sigmaSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                </Button>
              </div>
              
              {/* Resultados da busca */}
              {sigmaSearchResults.length > 0 && (
                <div className="space-y-2">
                  {sigmaSearchResults.map((client, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{client.username}</p>
                        <p className="text-xs text-muted-foreground">
                          {client.sigma_package_name} • Expira: {client.expiration_date || 'N/A'}
                        </p>
                      </div>
                      <Button size="sm" onClick={() => applySigmaClient(client)}>
                        Usar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-muted-foreground">
                Busque pelo username para preencher automaticamente os dados do cliente.
              </p>
            </div>
          )}
          
          <div className="space-y-4 py-4">
            {!editingCustomer && importData && (
              <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/30 text-sm">
                <span className="text-purple-300">
                  Importado de {importData.panel || 'painel'}: <b>{importData.username}</b> — confira os dados e preencha o que faltar.
                </span>
                <button type="button" title="Desfazer importação" onClick={() => setImportData(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 min-h-[20px]">
                  <Label htmlFor="name">{(customerForm.sigma_username || customerForm.panel_customer_id || customerForm.sigma_login_integration_id) ? 'Usuário no painel *' : <>Usuário / Login <span className="text-muted-foreground font-normal">(opcional)</span></>}</Label>
                  {!editingCustomer && (
                    <button type="button" onClick={() => setImportOpen(true)} data-testid="import-customer-btn"
                      className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
                      <Download className="w-3 h-3" /> Importar
                    </button>
                  )}
                </div>
                <Input
                  id="name"
                  placeholder="usuario_do_painel"
                  value={customerForm.name}
                  onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                />
                <p className="text-[11px] text-muted-foreground">É o que compara e renova no Sigma/PainelFoda. Não altere se o cliente for de integração.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone (DDI+DDD) *</Label>
                <Input
                  id="phone"
                  placeholder="5521999999999"
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer_name">Nome do cliente {(customerForm.sigma_login_integration_id || customerForm.sigma_username || customerForm.panel_customer_id) ? <span className="text-muted-foreground font-normal">(opcional)</span> : <span className="text-red-500">*</span>}</Label>
              <Input
                id="customer_name"
                placeholder="Ex.: João Silva — nome que aparece na lista"
                value={customerForm.customer_name || ''}
                onChange={(e) => setCustomerForm({ ...customerForm, customer_name: e.target.value })}
              />
              <p className="text-[11px] text-muted-foreground">Nome amigável só para você. Não afeta o vínculo com o painel.</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plano *</Label>
                <Select 
                  value={customerForm.plan_id} 
                  onValueChange={(v) => setCustomerForm({ ...customerForm, plan_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o plano" />
                  </SelectTrigger>
                  <SelectContent>
                    {plansForCustomer(customerForm).map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - {formatCurrency(plan.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Conexão WhatsApp</Label>
                <Select 
                  value={customerForm.connection_id} 
                  onValueChange={(v) => setCustomerForm({ ...customerForm, connection_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conexão" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.filter(c => c.status === 'connected').map(conn => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.name} {conn.phone_number ? `(${conn.phone_number})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expiration_date">Data de Vencimento *</Label>
                <Input
                  id="expiration_date"
                  type="date"
                  value={customerForm.expiration_date}
                  onChange={(e) => setCustomerForm({ ...customerForm, expiration_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiration_time">Hora do Envio</Label>
                <Input
                  id="expiration_time"
                  type="time"
                  value={customerForm.expiration_time}
                  onChange={(e) => setCustomerForm({ ...customerForm, expiration_time: e.target.value })}
                />
              </div>
            </div>

            {/* Acessos vinculados (só ao editar) */}
            {editingCustomer && (
              <LinkedAccountsSection customerId={editingCustomer.id} onChanged={() => fetchData()} />
            )}

            {/* Valor Personalizado */}
            <div className="space-y-2">
              <Label htmlFor="custom_price">Valor Personalizado (R$)</Label>
              <Input
                id="custom_price"
                type="number"
                step="0.01"
                placeholder={`Deixe vazio para usar valor do plano (${formatCurrency(plans.find(p => p.id === customerForm.plan_id)?.price || 0)})`}
                value={customerForm.custom_price}
                onChange={(e) => setCustomerForm({ ...customerForm, custom_price: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Se preenchido, o cliente verá apenas este valor no link de pagamento. Deixe vazio para usar o valor do plano.
              </p>
            </div>
            
            {/* Dias de envio */}
            <div className="space-y-3">
              <Label className="text-base">Dias de Envio de Fatura</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: 'send_7_days', label: '7 dias antes' },
                  { key: 'send_5_days', label: '5 dias antes' },
                  { key: 'send_4_days', label: '4 dias antes' },
                  { key: 'send_3_days', label: '3 dias antes' },
                  { key: 'send_2_days', label: '2 dias antes' },
                  { key: 'send_1_day', label: '1 dia antes' },
                  { key: 'send_on_day', label: 'No dia' },
                  { key: 'send_1_day_after', label: '1 dia depois' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                    <Switch
                      id={key}
                      checked={customerForm[key]}
                      onCheckedChange={(checked) => setCustomerForm({ ...customerForm, [key]: checked })}
                    />
                    <Label htmlFor={key} className="text-sm cursor-pointer">{label}</Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="birthday">Aniversário (opcional)</Label>
              <Input
                id="birthday"
                placeholder="DD/MM"
                maxLength={5}
                value={customerForm.birthday || ''}
                onChange={(e) => {
                  let v = e.target.value.replace(/[^\d]/g, '');
                  if (v.length > 2) v = v.slice(0, 2) + '/' + v.slice(2, 4);
                  setCustomerForm({ ...customerForm, birthday: v });
                }}
                className="h-9"
                data-testid="customer-birthday-input"
              />
              <p className="text-xs text-muted-foreground">
                Dia e mês de aniversário do cliente (ex: 15/03)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                placeholder="Observações sobre o cliente..."
                value={customerForm.notes}
                onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
              />
            </div>

            {/* Link de Pagamento */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <Label htmlFor="payment_link" className="text-sm font-medium">Link de Pagamento</Label>
                <p className="text-xs text-muted-foreground">
                  Habilita o link de pagamento para este cliente
                </p>
              </div>
              <Switch
                id="payment_link"
                checked={customerForm.payment_link_enabled}
                onCheckedChange={(checked) => setCustomerForm({ ...customerForm, payment_link_enabled: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerModalOpen(false)}>Cancelar</Button>
            <Button onClick={saveCustomer} disabled={saving}>
              {saving ? 'Salvando...' : (editingCustomer ? 'Salvar' : 'Cadastrar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Modal */}
      {/* Excluir plano em uso — lista os clientes afetados */}
      <Dialog open={!!deletePlanTarget} onOpenChange={(o) => { if (!o && !deletingPlan) setDeletePlanTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir plano "{deletePlanTarget?.plan?.name}"?</DialogTitle>
            <DialogDescription>
              {deletePlanTarget?.loading
                ? 'Carregando os clientes que usam este plano…'
                : (deletePlanTarget?.plan?.sigma_login_integration_id || deletePlanTarget?.plan?.sigma_package_id)
                ? `⚠️ Este é um plano de integração. Ao excluí-lo, os ${deletePlanTarget?.customers?.length || 0} cliente(s) abaixo serão EXCLUÍDOS do NexuZap (recuperáveis por 30 dias). Isso não altera nada no painel.`
                : `${deletePlanTarget?.customers?.length || 0} cliente(s) usam este plano e ficarão sem plano no NexuZap. Isso não altera nada no painel; para renovar de novo, será preciso definir um plano para cada um.`}
            </DialogDescription>
          </DialogHeader>
          {deletePlanTarget?.loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-border/60 divide-y divide-border/50">
              {(deletePlanTarget?.customers || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name || '(sem nome)'}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.phone || '—'}{c.expiration_date ? ` · vence ${c.expiration_date}` : ''}</p>
                  </div>
                  {c.is_panel && <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-500/30 shrink-0">Painel</Badge>}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePlanTarget(null)} disabled={deletingPlan}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDeletePlanForce} disabled={deletingPlan || deletePlanTarget?.loading}>
              {deletingPlan && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {(deletePlanTarget?.plan?.sigma_login_integration_id || deletePlanTarget?.plan?.sigma_package_id)
                ? 'Excluir plano e clientes' : 'Excluir mesmo assim'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={planModalOpen} onOpenChange={setPlanModalOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {(plansView === 'integracao' || plansView === 'novo') && !editingPlan && (
                <button onClick={() => { setPlansView('grupos'); setPlansGrupoSel(null); }} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /></button>
              )}
              {editingPlan ? 'Editar plano' : (plansView === 'grupos' ? 'Planos' : plansView === 'novo' ? 'Novo plano' : (plansGrupoSel?.nome || 'Planos'))}
            </DialogTitle>
            <DialogDescription>
              {plansView === 'grupos' ? 'Escolha um grupo para ver e organizar os planos.' : 'Planos definem preço e duração das renovações.'}
            </DialogDescription>
          </DialogHeader>

          {/* VISTA 1: GRUPOS (integrações) */}
          {!editingPlan && plansView === 'grupos' && (
            <div className="space-y-2">
              {gruposDePlanos().map((g) => {
                const tint = g.provider === 'painelfoda' ? 'text-sky-400' : g.provider === 'geradorpro' ? 'text-fuchsia-400' : g.provider === 'fshorts' ? 'text-cyan-400' : g.provider === 'dominus' ? 'text-amber-400' : g.provider === 'sigma' ? 'text-emerald-400' : 'text-muted-foreground';
                return (
                  <button key={g.key} onClick={() => { setPlansGrupoSel(g); setPlansView('integracao'); }}
                    className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-border/60 hover:border-primary/40 transition-colors text-left">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center ${tint}`}><Package className="w-5 h-5" /></div>
                      <div>
                        <p className="font-medium">{g.nome}</p>
                        <p className="text-xs text-muted-foreground">{g.provider_label || (g.provider || 'local')} · {g.count} plano(s)</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}

          {/* VISTA 2: PLANOS DE UM GRUPO (com ordenação) */}
          {!editingPlan && plansView === 'integracao' && plansGrupoSel && (
            <div className="space-y-3">
              {(() => {
                const isSigma = plansGrupoSel.provider === 'sigma';
                const isDominus = plansGrupoSel.provider === 'dominus';
                const lista = planosDoGrupo(plansGrupoSel).slice().sort((a,b)=>((a.sort_order??99999)-(b.sort_order??99999))||((a.price||0)-(b.price||0)));
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Arraste ou use o número para ordenar.</p>
                      {isSigma ? (
                        <Button size="sm" onClick={() => { setPlanModalOpen(false); setSigmaPlansOpen(true); }}>
                          <Download className="w-4 h-4 mr-1" /> Importar do painel Sigma
                        </Button>
                      ) : isDominus ? (
                        <Button size="sm" onClick={() => {
                          setDominusImport({ open: true, integ: plansGrupoSel, loading: true, packages: [], precos: {} });
                          api.get(`/sigma/login/${plansGrupoSel.id}/panel-packages`)
                            .then((r) => setDominusImport((d) => ({ ...d, loading: false, packages: r.data.packages || [] })))
                            .catch(() => setDominusImport((d) => ({ ...d, loading: false, packages: [] })));
                        }}><Download className="w-4 h-4 mr-1" /> Importar planos do Dominus</Button>
                      ) : (
                        <Button size="sm" onClick={() => {
                          setPlansView('novo');
                          setPlanForm((f) => ({ ...f, sigma_login_integration_id: plansGrupoSel.id === 'none' ? '' : plansGrupoSel.id, sigma_login_package_id: '', name: '', price: '', duration_months: 1 }));
                          if (plansGrupoSel.id !== 'none' && !panelPackages[plansGrupoSel.id]) {
                            setPanelPackages((pp) => ({ ...pp, [plansGrupoSel.id]: { loading: true, packages: [] } }));
                            api.get(packagesEndpoint(plansGrupoSel.id))
                              .then((r) => setPanelPackages((pp) => ({ ...pp, [plansGrupoSel.id]: { loading: false, packages: r.data.packages || [], note: r.data.note } })))
                              .catch(() => setPanelPackages((pp) => ({ ...pp, [plansGrupoSel.id]: { loading: false, packages: [], note: 'Não consegui ler os pacotes.' } })));
                          }
                        }}><Plus className="w-4 h-4 mr-1" /> Novo plano</Button>
                      )}
                    </div>
                    {lista.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Nenhum plano neste grupo ainda.</p>}
                    <div ref={scrollBoxRef} onDragOver={onDragOverBox} className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                      {(ordemDrag || lista).map((plan, idx) => {
                        const arrastando = dragIdRef.current === plan.id && ordemDrag;
                        return (
                        <div key={plan.id} draggable
                          ref={(el) => { if (el) itemRefs.current[plan.id] = el; else delete itemRefs.current[plan.id]; }}
                          onDragStart={(e) => { onDragStartPlano(ordemDrag || lista, idx); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnter={() => onDragEnterPlano(idx)}
                          onDragOver={(e) => e.preventDefault()}
                          onDragEnd={onDropPlano}
                          onDrop={onDropPlano}
                          style={{ opacity: arrastando ? 0.5 : 1, position: 'relative', zIndex: arrastando ? 10 : 1 }}
                          className={`flex items-center gap-2 p-3 rounded-xl border bg-background cursor-grab active:cursor-grabbing ${arrastando ? 'border-primary shadow-lg' : 'border-border/60 hover:border-border'}`}>
                          <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                          {editOrderIdx === plan.id ? (
                            <input type="number" autoFocus defaultValue={idx + 1} min="1" max={lista.length}
                              onBlur={(e) => { const to = Math.max(1, Math.min(lista.length, parseInt(e.target.value) || 1)) - 1; if (to !== idx) moverPlano(lista, idx, to); setEditOrderIdx(null); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditOrderIdx(null); }}
                              className="w-10 h-8 text-center text-sm rounded-lg border border-primary bg-background shrink-0" />
                          ) : (
                            <button onClick={() => setEditOrderIdx(plan.id)} title="Clique para mudar a posição"
                              className="w-8 h-8 rounded-lg bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center shrink-0 hover:bg-primary/20 transition-colors">
                              {idx + 1}
                            </button>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate text-sm">{plan.name}</p>
                            <p className="text-xs text-muted-foreground">{formatCurrency(plan.price)} · {plan.duration_months || 1} {(plan.duration_months || 1) === 1 ? 'mês' : 'meses'}{plan.sigma_login_connections > 1 ? ` · ${plan.sigma_login_connections} telas` : ''}</p>
                          </div>
                          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => openPlanModal(plan)}><Edit className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-red-500 hover:bg-red-500/10" onClick={() => deletePlan(plan)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* VISTA 3: NOVO/EDITAR PLANO (formulário) */}
          {(editingPlan || plansView === 'novo') && (
          <div className="space-y-4">


            {/* Vincular a um painel */}
            {panelIntegrations.length > 0 && (
              <div className="rounded-xl border border-border/60 p-3 space-y-3 bg-muted/20">
                <div className="space-y-1.5">
                  <Label className="text-xs">Painel (opcional)</Label>
                  <Select
                    value={planForm.sigma_login_integration_id || 'none'}
                    onValueChange={(v) => {
                      const id = v === 'none' ? '' : v;
                      setPlanForm({ ...planForm, sigma_login_integration_id: id, sigma_login_package_id: '' });
                      if (id && !panelPackages[id]) {
                        setPanelPackages((pp) => ({ ...pp, [id]: { loading: true, packages: [] } }));
                        api.get(packagesEndpoint(id))
                          .then((r) => setPanelPackages((pp) => ({ ...pp, [id]: { loading: false, packages: r.data.packages || [], note: r.data.note } })))
                          .catch(() => setPanelPackages((pp) => ({ ...pp, [id]: { loading: false, packages: [], note: 'Não consegui ler os pacotes agora.' } })));
                      }
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Plano local (sem painel)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Plano local (sem painel)</SelectItem>
                      {panelIntegrations.filter((i) => i.provider !== 'sigma').map((i) => (
                        <SelectItem key={i.id} value={i.id}>{i.name} — {i.provider_label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {(() => {
                  const integ = panelIntegrations.find((i) => i.id === planForm.sigma_login_integration_id);
                  if (!integ) return null;
                  // EDITANDO um plano existente: o pacote já está vinculado, não busca nada — só duração
                  // Painéis externos (Gerador Pro, FShorts) e os IMPORTADOS (Sigma, Dominus): idem
                  if (editingPlan || integ.provider === 'geradorpro' || integ.provider === 'fshorts' || integ.provider === 'sigma' || integ.provider === 'dominus' || integ.provider === 'natv') {
                    return (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Duração (meses)</Label>
                        <Select value={String(planForm.duration_months || 1)}
                          onValueChange={(v) => setPlanForm((f) => ({ ...f, duration_months: parseInt(v) }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1,2,3,6,12].map((m) => <SelectItem key={m} value={String(m)}>{m} {m===1?'mês':'meses'}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground">A renovação nesse painel usa essa duração (em meses).</p>
                      </div>
                    );
                  }
                  const pk = panelPackages[integ.id] || { loading: false, packages: [] };
                  return (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Pacote do {integ.provider_label}</Label>
                      {pk.loading ? (
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Lendo os pacotes do painel…</p>
                      ) : pk.packages.length > 0 ? (
                        <Select
                          value={planForm.sigma_login_package_id || ''}
                          onValueChange={(v) => {
                            const chosen = pk.packages.find((p) => p.id === v);
                            setPlanForm((f) => ({ ...f, sigma_login_package_id: v,
                              name: f.name || (chosen ? chosen.name : '') }));
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Escolha o pacote (Mensal, Anual...)" /></SelectTrigger>
                          <SelectContent>
                            {pk.packages.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.name} · {p.credits} crédito(s)</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">Defina o preço e a duração abaixo para criar o plano.</p>
                      )}
                      <div className="pt-1">
                        <Label className="text-xs">Telas</Label>
                        <Input type="number" min="1" className="h-8 mt-1 w-24" value={planForm.sigma_login_connections}
                          onChange={(e) => setPlanForm({ ...planForm, sigma_login_connections: e.target.value })} />
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="plan_name">Nome do plano</Label>
              <Input id="plan_name" placeholder="Ex: Mensal, Trimestral..." value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plan_price">Valor (R$)</Label>
                <Input id="plan_price" type="number" step="0.01" placeholder="99.90" value={planForm.price}
                  onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plan_duration">Duração (meses)</Label>
                <Input id="plan_duration" type="number" min="1" placeholder="1"
                  value={plansGrupoSel?.provider === 'gerenciaapp' ? 1 : planForm.duration_months}
                  disabled={plansGrupoSel?.provider === 'gerenciaapp'}
                  onChange={(e) => setPlanForm({ ...planForm, duration_months: e.target.value })} />
                {plansGrupoSel?.provider === 'gerenciaapp' && (
                  <p className="text-[11px] text-amber-500">O GerenciaApp só permite renovação de 1 mês por vez.</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan_custo" className="flex items-center gap-1.5">💰 Seu custo mensal por cliente (R$) <span className="text-[10px] text-muted-foreground font-normal">opcional</span></Label>
              <Input id="plan_custo" type="number" step="0.01" min="0" placeholder="Ex: 2.00" value={planForm.custo_mensal}
                onChange={(e) => setPlanForm({ ...planForm, custo_mensal: e.target.value })} />
              <p className="text-[11px] text-muted-foreground">Quanto este plano te custa por cliente/mês. Usado no cálculo de custo mensal no Financeiro. Deixe vazio se não quiser calcular.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan_desc">Descrição (opcional)</Label>
              <Input id="plan_desc" placeholder="Descrição do plano..." value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
            </div>
          </div>
          )}

          {(editingPlan || plansView === 'novo') && (
          <DialogFooter>
            <Button variant="outline" onClick={() => { if (editingPlan) { setEditingPlan(null); setPlansView(plansGrupoSel ? 'integracao' : 'grupos'); } else { setPlansView('integracao'); } }}>Voltar</Button>
            <Button onClick={savePlan} disabled={saving}>
              {saving ? 'Salvando...' : (editingPlan ? 'Salvar' : 'Criar plano')}
            </Button>
          </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Renew Modal */}
      <Dialog open={renewModalOpen} onOpenChange={setRenewModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Renovar Cliente</DialogTitle>
            <DialogDescription>
              {renewingCustomer?.name}
              {isSigmaCustomer(renewingCustomer) && (
                <Badge variant="outline" className="ml-2 text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">Sigma</Badge>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Multi-acessos: escolher quais renovar */}
          {renewAccounts.length > 0 && (
            <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold flex items-center gap-1.5">
                  <span className="text-primary">🔗</span> Acessos a renovar
                </span>
                <div className="flex gap-2 text-xs">
                  <button type="button" className="text-primary" onClick={() => { const s = {}; renewAccounts.forEach(a => s[a.account_id] = true); setRenewAccSel(s); }}>Todos</button>
                  <button type="button" className="text-muted-foreground" onClick={() => setRenewAccSel({})}>Nenhum</button>
                </div>
              </div>
              {renewAccounts.map((a) => (
                <label key={a.account_id} className="flex items-center gap-2.5 rounded-lg border border-border/50 px-2.5 py-2 cursor-pointer hover:border-primary/40">
                  <input type="checkbox" checked={!!renewAccSel[a.account_id]}
                    onChange={(e) => setRenewAccSel({ ...renewAccSel, [a.account_id]: e.target.checked })}
                    className="w-4 h-4 accent-primary" />
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${a.provider === 'painelfoda' ? 'bg-sky-500/15 text-sky-400 border-sky-500/25' : a.provider === 'geradorpro' ? 'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/25' : a.provider === 'fshorts' ? 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25' : a.provider === 'dominus' ? 'bg-amber-500/15 text-amber-400 border-amber-500/25' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25'}`}>{a.provider_label || 'Painel'}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{a.sigma_username || a.label}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{a.plan_name || 'sem plano'}</p>
                  </div>
                </label>
              ))}
              <p className="text-[11px] text-muted-foreground">Cada acesso é renovado no seu painel com o plano dele.</p>
            </div>
          )}
          
          <div className="space-y-4 py-4">
            {/* Aviso de créditos insuficientes */}
            {sigmaCreditsWarning && (
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-sm text-orange-400 font-medium mb-2">⚠️ Atenção</p>
                <p className="text-sm text-orange-300">{sigmaCreditsWarning.message}</p>
                <div className="flex gap-2 mt-3">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setSigmaCreditsWarning(null)}
                    className="text-xs"
                  >
                    Voltar
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={renewCustomer}
                    disabled={saving}
                    className="text-xs bg-orange-500 hover:bg-orange-600"
                  >
                    {saving ? 'Renovando...' : 'Renovar mesmo assim'}
                  </Button>
                </div>
              </div>
            )}
            
            {!sigmaCreditsWarning && (
              <>
                {/* Para clientes Sigma - apenas planos */}
                {isSigmaCustomer(renewingCustomer) ? (
                  <div className="space-y-3">
                    <Label className="font-medium flex items-center gap-2">
                      🎯 Selecione o Plano para Renovação
                    </Label>
                    <Select 
                      value={renewForm.plan_id} 
                      onValueChange={(v) => setRenewForm({ ...renewForm, type: 'plan', plan_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o plano" />
                      </SelectTrigger>
                      <SelectContent>
                        {renewablePlansFor(renewingCustomer).map(plan => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name} - R$ {plan.price?.toFixed(2).replace('.', ',')} ({plan.duration_months || 1} {plan.duration_months === 1 ? 'mês' : 'meses'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {renewablePlansFor(renewingCustomer).length === 0 && (
                      <p className="text-xs text-amber-600">
                        ⚠️ Nenhum plano disponível. Crie os planos em Minhas Integrações → Planos do painel.
                      </p>
                    )}
                    {renewingCustomer?.custom_price !== null && renewingCustomer?.custom_price !== undefined ? (
                      <p className="text-xs text-amber-500">Cliente com valor personalizado: renova só pelo plano atual.</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        O cliente será renovado pelo tempo do plano selecionado e o plano será atualizado.
                      </p>
                    )}
                  </div>
                ) : (
                  // Para clientes não-Sigma - opções normais
                  <>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          id="renew_months"
                          name="renew_type"
                          checked={renewForm.type === 'months'}
                          onChange={() => setRenewForm({ ...renewForm, type: 'months' })}
                        />
                        <Label htmlFor="renew_months">Por quantidade de meses</Label>
                      </div>
                      
                      {renewForm.type === 'months' && (
                        <div className="pl-6">
                          <Select 
                            value={renewForm.months.toString()} 
                            onValueChange={(v) => setRenewForm({ ...renewForm, months: parseInt(v) })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[...new Set([1, 2, 3, 6, 12, renewForm.months || 1])].sort((a, b) => a - b).map(m => (
                                <SelectItem key={m} value={m.toString()}>
                                  {m} {m === 1 ? 'mês' : 'meses'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          id="renew_date"
                          name="renew_type"
                          checked={renewForm.type === 'date'}
                          onChange={() => setRenewForm({ ...renewForm, type: 'date' })}
                        />
                        <Label htmlFor="renew_date">Por data específica</Label>
                      </div>
                      
                      {renewForm.type === 'date' && (
                        <div className="pl-6">
                          <Input
                            type="date"
                            value={renewForm.new_date}
                            onChange={(e) => setRenewForm({ ...renewForm, new_date: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Notificar no WhatsApp (opcional) */}
          {!isSimple && renewingCustomer?.phone && (
            <label className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5 cursor-pointer mt-1">
              <span className="text-sm flex items-center gap-2">📲 Avisar o cliente no WhatsApp</span>
              <input type="checkbox" checked={renewNotify} onChange={(e) => setRenewNotify(e.target.checked)} className="w-4 h-4 accent-primary" />
            </label>
          )}

          {!sigmaCreditsWarning && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenewModalOpen(false)}>Cancelar</Button>
              <Button onClick={checkSigmaCreditsAndRenew} disabled={saving || checkingCredits}>
                {checkingCredits ? 'Verificando...' : saving ? 'Renovando...' : 'Renovar'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configurações de Clientes</DialogTitle>
            <DialogDescription>
              Configure as mensagens, dias padrão e conexão
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="connection" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="connection">Conexão</TabsTrigger>
              <TabsTrigger value="days">Dias de Envio</TabsTrigger>
              <TabsTrigger value="messages">Mensagens</TabsTrigger>
              <TabsTrigger value="referral">Indicação</TabsTrigger>
            </TabsList>

            <TabsContent value="connection" className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-sm text-yellow-500">
                  ⚠️ Ao alterar a conexão padrão, <strong>TODOS os clientes</strong> serão atualizados automaticamente para usar a nova conexão.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Conexão WhatsApp Padrão</Label>
                <Select 
                  value={settings.default_connection_id || ''} 
                  onValueChange={(v) => setSettings({ ...settings, default_connection_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conexão padrão" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.filter(c => c.status === 'connected').map(conn => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.name} {conn.phone_number ? `(${conn.phone_number})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Esta conexão será usada para enviar as cobranças de todos os clientes.
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="days" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground mb-4">
                Configure os dias padrão para novos clientes. Use as variáveis: {'{nome}'}, {'{plano}'}, {'{valor}'}, {'{vencimento}'}, {'{dias_restantes}'}, {'{Link_de_pagamento}'}
              </p>
              
              <div className="space-y-2">
                <Label>Horário Padrão de Envio</Label>
                <Input
                  type="time"
                  value={settings.default_expiration_time || '09:00'}
                  onChange={(e) => setSettings({ ...settings, default_expiration_time: e.target.value })}
                  className="w-32"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'default_send_7_days', label: '7 dias antes' },
                  { key: 'default_send_5_days', label: '5 dias antes' },
                  { key: 'default_send_4_days', label: '4 dias antes' },
                  { key: 'default_send_3_days', label: '3 dias antes' },
                  { key: 'default_send_2_days', label: '2 dias antes' },
                  { key: 'default_send_1_day', label: '1 dia antes' },
                  { key: 'default_send_on_day', label: 'No dia' },
                  { key: 'default_send_1_day_after', label: '1 dia depois' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                    <Switch
                      id={key}
                      checked={settings[key] ?? true}
                      onCheckedChange={(checked) => setSettings({ ...settings, [key]: checked })}
                    />
                    <Label htmlFor={key} className="text-sm cursor-pointer">{label}</Label>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="messages" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground mb-4">
                Variáveis disponíveis: <code className="bg-muted px-1 rounded">{'{nome}'}</code> <code className="bg-muted px-1 rounded">{'{plano}'}</code> <code className="bg-muted px-1 rounded">{'{valor}'}</code> <code className="bg-muted px-1 rounded">{'{vencimento}'}</code> <code className="bg-muted px-1 rounded">{'{dias_restantes}'}</code> <code className="bg-muted px-1 rounded">{'{Link_de_pagamento}'}</code>
              </p>

              {/* Botão de Renovar nas cobranças (ativado por padrão) */}
              <label className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/[0.03] px-4 py-3 cursor-pointer mb-2">
                <div>
                  <span className="text-sm font-medium flex items-center gap-2">💠 Botão &quot;Renovar&quot; nas cobranças</span>
                  <p className="text-xs text-muted-foreground mt-0.5">Já vem ativado: as cobranças de clientes com link de pagamento levam um botão que abre o pagamento. Desligue se não quiser.</p>
                </div>
                <Switch checked={settings.billing_button_enabled !== false} onCheckedChange={(v) => setSettings({ ...settings, billing_button_enabled: v })} />
              </label>

              {/* COMPROVANTE PDF */}
              <label className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/[0.03] px-4 py-3 cursor-pointer mb-2">
                <div>
                  <span className="text-sm font-medium flex items-center gap-2">📄 Enviar comprovante (PDF)</span>
                  <p className="text-xs text-muted-foreground mt-0.5">Após a renovação, envia o comprovante em PDF numa segunda mensagem. Use {'{comprovante}'} nas mensagens para o link.</p>
                </div>
                <Switch checked={!!settings.enviar_comprovante} onCheckedChange={(v) => setSettings({ ...settings, enviar_comprovante: v })} />
              </label>

              {/* DELAY ENTRE COBRANÇAS */}
              <div className="rounded-xl border border-border bg-muted/20 px-4 py-3 mb-2">
                <p className="text-sm font-medium flex items-center gap-2">⏱️ Intervalo entre cobranças</p>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">Ao cobrar vários clientes de uma vez, espera este tempo (em segundos) entre cada mensagem. 0 = envia tudo junto.</p>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" max="300" value={settings.billing_delay_seconds ?? 0}
                    onChange={(e) => setSettings({ ...settings, billing_delay_seconds: parseInt(e.target.value) || 0 })}
                    className="w-28 h-9" />
                  <span className="text-xs text-muted-foreground">segundos entre cada</span>
                </div>
              </div>

              {/* IMAGENS DA COBRANÇA (imagem.app) */}
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] px-4 py-3 mb-2 space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="text-sm font-medium flex items-center gap-2">🖼️ Imagem nas cobranças</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Envia uma imagem com o texto como legenda (+ botão). Uma padrão, ou uma por dia.</p>
                  </div>
                  <Switch checked={!!settings.billing_image_enabled} onCheckedChange={(v) => setSettings({ ...settings, billing_image_enabled: v })} />
                </label>

                {settings.billing_image_enabled && (
                  <div className="space-y-3 pt-1">
                    {/* imagem padrão + por dia */}
                    <BillingImageField label="Imagem padrão (todas)" field="billing_image_default" settings={settings} setSettings={setSettings} uploadImg={uploadBillingImage} />
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { f: 'billing_image_7_days', l: '7 dias' },
                        { f: 'billing_image_5_days', l: '5 dias' },
                        { f: 'billing_image_3_days', l: '3 dias' },
                        { f: 'billing_image_1_day', l: '1 dia' },
                        { f: 'billing_image_on_day', l: 'No dia' },
                        { f: 'billing_image_1_day_after', l: '1 dia depois' },
                      ].map(({ f, l }) => (
                        <BillingImageField key={f} label={l} field={f} settings={settings} setSettings={setSettings} uploadImg={uploadBillingImage} small />
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Sem imagem específica no dia? Usa a padrão.</p>
                    <div className="pt-2 mt-2 border-t border-border">
                      <BillingImageField label="🎉 Imagem da renovação confirmada (separada)" field="billing_image_renewal" settings={settings} setSettings={setSettings} uploadImg={uploadBillingImage} />
                      <p className="text-[11px] text-muted-foreground mt-1">Enviada quando o cliente paga e renova. É independente — não usa a imagem padrão.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* PROGRAMA DE FIDELIDADE */}
              <div className="rounded-xl border border-border p-4 space-y-3">
                <label className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold flex items-center gap-1.5">🏆 Programa de Fidelidade</p>
                    <p className="text-[11px] text-muted-foreground">Cliente ganha selos ao renovar em dia e recebe desconto ao completar.</p>
                  </div>
                  <Switch checked={!!settings.fidelidade_enabled} onCheckedChange={(v) => setSettings({ ...settings, fidelidade_enabled: v })} />
                </label>
                {settings.fidelidade_enabled && (
                  <div className="space-y-3 pt-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Selos para a recompensa</Label>
                        <Input type="number" min="1" value={settings.fidelidade_selos_meta ?? 6} onChange={(e) => setSettings({ ...settings, fidelidade_selos_meta: parseInt(e.target.value) || 0 })} />
                      </div>
                      <div>
                        <Label className="text-xs">Antecedência p/ ganhar selo (dias)</Label>
                        <Input type="number" min="0" value={settings.fidelidade_antecedencia_dias ?? 0} onChange={(e) => setSettings({ ...settings, fidelidade_antecedencia_dias: parseInt(e.target.value) || 0 })} />
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground -mt-1">0 = basta renovar até o vencimento. 3 = precisa renovar 3+ dias antes.</p>
                    <div>
                      <Label className="text-xs">Recompensa ao completar</Label>
                      <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                        value={settings.fidelidade_recompensa_tipo || 'percentual'} onChange={(e) => setSettings({ ...settings, fidelidade_recompensa_tipo: e.target.value })}>
                        <option value="percentual">Desconto em % na renovação</option>
                        <option value="dias">Dias grátis a mais</option>
                        <option value="gratis">Uma renovação grátis</option>
                      </select>
                    </div>
                    {settings.fidelidade_recompensa_tipo !== 'gratis' && (
                      <div>
                        <Label className="text-xs">{settings.fidelidade_recompensa_tipo === 'dias' ? 'Quantos dias grátis' : 'Percentual de desconto (%)'}</Label>
                        <Input type="number" min="0" value={settings.fidelidade_recompensa_valor ?? (settings.fidelidade_recompensa_tipo === 'dias' ? 7 : 50)} onChange={(e) => setSettings({ ...settings, fidelidade_recompensa_valor: parseFloat(e.target.value) || 0 })} />
                      </div>
                    )}
                    <label className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2.5">
                      <div>
                        <span className="text-sm">Zerar selos se atrasar</span>
                        <p className="text-[10px] text-muted-foreground">Força o cliente a pagar em dia para não perder o progresso.</p>
                      </div>
                      <Switch checked={!!settings.fidelidade_zera_atraso} onCheckedChange={(v) => setSettings({ ...settings, fidelidade_zera_atraso: v })} />
                    </label>
                    <div className="rounded-lg bg-primary/5 border border-primary/20 p-2.5">
                      <p className="text-[11px] text-muted-foreground">Prévia: <span className="text-foreground">Renove em dia {settings.fidelidade_selos_meta ?? 6}x e ganhe {settings.fidelidade_recompensa_tipo === 'gratis' ? 'uma renovação grátis' : settings.fidelidade_recompensa_tipo === 'dias' ? `${settings.fidelidade_recompensa_valor ?? 7} dias grátis` : `${settings.fidelidade_recompensa_valor ?? 50}% de desconto`}!</span></p>
                    </div>
                    <div className="rounded-lg bg-muted/40 p-2.5">
                      <p className="text-[11px] text-muted-foreground mb-1">📝 Variáveis para usar nas suas mensagens:</p>
                      <div className="flex flex-wrap gap-1">
                        <code className="text-[10px] bg-background px-1.5 py-0.5 rounded">{'{selos}'}</code>
                        <code className="text-[10px] bg-background px-1.5 py-0.5 rounded">{'{selos_meta}'}</code>
                        <code className="text-[10px] bg-background px-1.5 py-0.5 rounded">{'{selos_faltam}'}</code>
                        <code className="text-[10px] bg-background px-1.5 py-0.5 rounded">{'{recompensa}'}</code>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1.5">Ex: "Você tem {'{selos}'} de {'{selos_meta}'} selos! Faltam {'{selos_faltam}'} para ganhar {'{recompensa}'}."</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {[
                  { key: 'msg_onboarding', label: '🎉 Boas-vindas (cliente novo)' },
                  { key: 'msg_7_days', label: 'Mensagem 7 dias antes' },
                  { key: 'msg_5_days', label: 'Mensagem 5 dias antes' },
                  { key: 'msg_4_days', label: 'Mensagem 4 dias antes' },
                  { key: 'msg_3_days', label: 'Mensagem 3 dias antes' },
                  { key: 'msg_2_days', label: 'Mensagem 2 dias antes' },
                  { key: 'msg_1_day', label: 'Mensagem 1 dia antes' },
                  { key: 'msg_on_day', label: 'Mensagem no dia' },
                  { key: 'msg_1_day_after', label: 'Mensagem 1 dia depois' },
                  { key: 'msg_renewal', label: 'Mensagem de Renovação' },
                ].map(({ key, label }) => {
                  const val = settings[key] || '';
                  const preview = val.trim() ? val.replace(/\n/g, ' ').slice(0, 60) + (val.length > 60 ? '…' : '') : 'Sem mensagem — clique para escrever';
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setEditingMsg({ key, label, value: val })}
                      className="w-full text-left rounded-xl border border-border/60 bg-card hover:border-primary/40 hover:bg-muted/30 transition-all p-3 flex items-center gap-3 group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <MessageSquare className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{label}</p>
                        <p className={`text-xs truncate ${val.trim() ? 'text-muted-foreground' : 'text-amber-500'}`}>{preview}</p>
                      </div>
                      <Edit className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
                    </button>
                  );
                })}
              </div>
            </TabsContent>
            
            {/* Aba de Indicação */}
            <TabsContent value="referral" className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  🎁 Sistema de Indicação: Seus clientes SIGMA podem indicar outros clientes e ganhar renovações gratuitas!
                </p>
              </div>
              
              {/* Ativar/Desativar */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <Label htmlFor="referral_enabled" className="text-base font-medium">Ativar Sistema de Indicação</Label>
                  <p className="text-sm text-muted-foreground">Quando ativado, a caixa de indicação aparece no link de renovação</p>
                </div>
                <Switch
                  id="referral_enabled"
                  checked={settings.referral_enabled || false}
                  onCheckedChange={(checked) => setSettings({ ...settings, referral_enabled: checked })}
                />
              </div>
              
              {settings.referral_enabled && (
                <>
                  {/* Quantidade de indicações */}
                  <div className="space-y-2">
                    <Label>Quantidade de indicações para ganhar renovação</Label>
                    <Select 
                      value={String(settings.referral_count_required || 1)} 
                      onValueChange={(v) => setSettings({ ...settings, referral_count_required: parseInt(v) })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} indicação{n > 1 ? 'ões' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      O indicador ganha renovação ao completar esse número de indicações
                    </p>
                  </div>
                  
                  {/* Plano de premiação */}
                  <div className="space-y-2">
                    <Label>Plano para renovação do indicador</Label>
                    <Select 
                      value={settings.referral_plan_id || ''} 
                      onValueChange={(v) => setSettings({ ...settings, referral_plan_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o plano de premiação" />
                      </SelectTrigger>
                      <SelectContent>
                        {plans.filter(isSigmaPlan).map(plan => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name} - {plan.duration_months} {plan.duration_months === 1 ? 'mês' : 'meses'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {plans.filter(isSigmaPlan).length === 0 && (
                      <p className="text-xs text-amber-600">
                        ⚠️ Nenhum plano com integração Sigma. Configure em "Planos".
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Este plano será usado para renovar o indicador quando completar as indicações
                    </p>
                  </div>
                  
                  {/* Mensagem de progresso */}
                  <div className="space-y-2">
                    <Label>Mensagem de Progresso</Label>
                    <Textarea
                      rows={3}
                      value={settings.referral_progress_message || ''}
                      onChange={(e) => setSettings({ ...settings, referral_progress_message: e.target.value })}
                      placeholder="Parabéns {nome}! Você já indicou {indicacoes_feitas} pessoa(s). Faltam {indicacoes_faltando} para ganhar {premio}!"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enviada quando o indicador recebe uma indicação mas ainda não completou. Variáveis: {'{nome}'}, {'{indicacoes_feitas}'}, {'{indicacoes_faltando}'}, {'{indicacoes_total}'}, {'{premio}'}
                    </p>
                  </div>
                  
                  {/* Mensagem de sucesso */}
                  <div className="space-y-2">
                    <Label>Mensagem de Sucesso</Label>
                    <Textarea
                      rows={3}
                      value={settings.referral_success_message || ''}
                      onChange={(e) => setSettings({ ...settings, referral_success_message: e.target.value })}
                      placeholder="🎉 Parabéns {nome}! Você completou suas indicações e ganhou {premio}! Seu novo vencimento é {vencimento}."
                    />
                    <p className="text-xs text-muted-foreground">
                      Enviada quando o indicador completa as indicações e ganha a renovação. Variáveis: {'{nome}'}, {'{premio}'}, {'{vencimento}'}
                    </p>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => saveSettings(true)} disabled={saving}>
              Salvar e Aplicar a Todos
            </Button>
            <Button onClick={() => saveSettings(false)} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Modal */}
      <Dialog open={campaignModalOpen} onOpenChange={setCampaignModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5" />
              Enviar Campanha para Clientes
            </DialogTitle>
            <DialogDescription>
              Envie uma mensagem para seus clientes por plano ou para todos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Aviso quando é campanha para clientes selecionados */}
            {campaignForm.target_mode === 'selected' && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5 text-sm">
                📢 Campanha para <b>{campaignForm.selected_ids.length} cliente(s) selecionado(s)</b>.
                <button type="button" className="ml-2 text-xs text-muted-foreground underline"
                  onClick={() => setCampaignForm(f => ({ ...f, target_mode: 'plan' }))}>usar por plano</button>
              </div>
            )}

            {/* Selecionar planos (múltipla seleção) — oculto no modo selecionados */}
            <div className="space-y-2" style={{ display: campaignForm.target_mode === 'selected' ? 'none' : 'block' }}>
              <Label>Planos (selecione um ou mais)</Label>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-muted/50 rounded">
                  <input
                    type="checkbox"
                    checked={campaignForm.plan_ids.length === 0}
                    onChange={() => setCampaignForm({ ...campaignForm, plan_ids: [] })}
                    className="rounded"
                  />
                  <span className="flex-1">Todos os planos</span>
                  <Badge variant="outline">
                    {campaignForm.status_filter !== 'all' 
                      ? customers.filter(c => c.status === campaignForm.status_filter).length
                      : customers.length}
                  </Badge>
                </label>
                {plans.map(plan => {
                  let count = customers.filter(c => c.plan_id === plan.id);
                  if (campaignForm.status_filter !== 'all') {
                    count = count.filter(c => c.status === campaignForm.status_filter);
                  }
                  const isSelected = campaignForm.plan_ids.includes(plan.id);
                  return (
                    <label key={plan.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-muted/50 rounded">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          if (isSelected) {
                            setCampaignForm({ 
                              ...campaignForm, 
                              plan_ids: campaignForm.plan_ids.filter(id => id !== plan.id) 
                            });
                          } else {
                            setCampaignForm({ 
                              ...campaignForm, 
                              plan_ids: [...campaignForm.plan_ids, plan.id] 
                            });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="flex-1">{plan.name}</span>
                      <Badge variant="outline">{count.length}</Badge>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {(() => {
                  let filteredCustomers = customers;
                  
                  // Filtrar por planos selecionados
                  if (campaignForm.plan_ids.length > 0) {
                    filteredCustomers = filteredCustomers.filter(c => campaignForm.plan_ids.includes(c.plan_id));
                  }
                  
                  // Filter by status
                  if (campaignForm.status_filter !== 'all') {
                    filteredCustomers = filteredCustomers.filter(c => c.status === campaignForm.status_filter);
                  }
                  
                  return `${filteredCustomers.length} cliente(s) receberão a mensagem`;
                })()}
              </p>
            </div>

            {/* Filtro por status */}
            <div className="space-y-2">
              <Label>Filtrar por Status</Label>
              <Select 
                value={campaignForm.status_filter} 
                onValueChange={(v) => setCampaignForm({ ...campaignForm, status_filter: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="active">Apenas ativos</SelectItem>
                  <SelectItem value="expiring">Apenas vencendo</SelectItem>
                  <SelectItem value="expired">Apenas vencidos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tipo de mensagem */}
            <div className="space-y-3">
              <Label>Tipo de Mensagem</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="campaign_mode"
                    checked={campaignForm.mode === 'new'}
                    onChange={() => setCampaignForm({ ...campaignForm, mode: 'new' })}
                  />
                  <span>Nova mensagem</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="campaign_mode"
                    checked={campaignForm.mode === 'existing'}
                    onChange={() => setCampaignForm({ ...campaignForm, mode: 'existing' })}
                  />
                  <span>Usar campanha existente</span>
                </label>
              </div>
            </div>

            {/* Mensagem nova ou selecionar campanha */}
            {campaignForm.mode === 'new' ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Mensagem</Label>
                  <div className="flex gap-2">
                    {/* Seletor de Template */}
                    {templates.length > 0 && (
                      <Select onValueChange={applyTemplateToMessage}>
                        <SelectTrigger className="h-8 w-[140px] text-xs">
                          <SelectValue placeholder="📝 Templates" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(t => (
                            <SelectItem key={t.id} value={t.id} className="text-xs">
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={enhanceTextWithAI}
                      disabled={enhancingText || !campaignForm.message.trim()}
                      className="text-purple-500 hover:text-purple-400 border-purple-500/30"
                    >
                      {enhancingText ? (
                        <>
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          Melhorando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3 mr-1" />
                          Melhorar com IA
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <Textarea
                  placeholder="Digite sua mensagem..."
                  value={campaignForm.message}
                  onChange={(e) => setCampaignForm({ ...campaignForm, message: e.target.value })}
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  Variáveis disponíveis: {'{nome}'}, {'{plano}'}, {'{valor}'}, {'{vencimento}'}, {'{Link_de_pagamento}'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Selecionar Campanha</Label>
                <Select 
                  value={campaignForm.campaign_id} 
                  onValueChange={(v) => setCampaignForm({ ...campaignForm, campaign_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma campanha" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map(campaign => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {campaignForm.campaign_id && (
                  <div className="p-3 rounded bg-muted/50 text-sm">
                    <p className="font-medium mb-1">Preview:</p>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {campaigns.find(c => c.id === campaignForm.campaign_id)?.message?.substring(0, 200)}
                      {campaigns.find(c => c.id === campaignForm.campaign_id)?.message?.length > 200 && '...'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Delay entre envios */}
            <div className="space-y-2">
              <Label>Intervalo entre envios (segundos)</Label>
              <Input
                type="number"
                min="1"
                max="60"
                value={campaignForm.delay_seconds}
                onChange={(e) => setCampaignForm({ ...campaignForm, delay_seconds: parseInt(e.target.value) || 5 })}
                className="w-24"
              />
              <p className="text-xs text-muted-foreground">
                Recomendado: 5-10 segundos para evitar bloqueios
              </p>
            </div>

            {/* Upload de Imagem (opcional) */}
            <div className="space-y-2">
              <ImageSelector
                value={campaignForm.imageUrl}
                onChange={(url, id) => setCampaignForm({ ...campaignForm, imageUrl: url, imageId: id })}
                label="Imagem (opcional)"
                category="campaign"
              />
              <p className="text-xs text-muted-foreground">
                A imagem será enviada junto com a mensagem como legenda
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampaignModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={sendCampaign} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Campanha
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação - Deletar Vencidos */}
      <Dialog open={deleteExpiredModalOpen} onOpenChange={setDeleteExpiredModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="w-5 h-5" />
              Apagar Clientes Vencidos
            </DialogTitle>
            <DialogDescription>
              Esta ação irá remover permanentemente todos os <strong>{expiredCustomers}</strong> clientes vencidos.
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">
                ⚠️ Atenção: Todos os dados dos clientes vencidos serão perdidos permanentemente.
              </p>
            </div>
            {expiredSummary.panel > 0 && (
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${deleteExpiredAlsoPanel ? 'border-red-500/40 bg-red-500/5' : 'border-border/60'}`}>
                <Switch checked={deleteExpiredAlsoPanel} onCheckedChange={setDeleteExpiredAlsoPanel} className="mt-0.5" data-testid="delete-expired-also-panel" />
                <span className="text-sm">
                  Excluir também no painel os <b>{expiredSummary.panel}</b> vencido(s) de integração
                  <span className="block text-xs text-muted-foreground mt-1">
                    No painel a exclusão é definitiva e o cliente perde o acesso. Sem marcar, eles saem só do NexuZap. Se o painel não responder para algum, ele não é excluído em nenhum lugar.
                  </span>
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteExpiredModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={deleteExpiredCustomers}
              disabled={deletingExpired}
            >
              {deletingExpired ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Apagando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Confirmar Exclusão
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Acompanhamento de Campanha */}
      <Dialog open={!!activeCampaign} onOpenChange={(open) => !open && closeCampaignProgress()}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Acompanhamento de Envio
            </DialogTitle>
            <DialogDescription>
              Acompanhe o progresso do envio da campanha em tempo real
            </DialogDescription>
          </DialogHeader>
          
          {campaignProgress && (
            <div className="flex-1 overflow-auto space-y-4">
              {/* Status e Progresso */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      campaignProgress.status === 'running' ? 'default' :
                      campaignProgress.status === 'paused' ? 'secondary' :
                      campaignProgress.status === 'completed' ? 'success' :
                      campaignProgress.status === 'stopped' ? 'destructive' : 'outline'
                    }>
                      {campaignProgress.status === 'running' && 'Enviando...'}
                      {campaignProgress.status === 'paused' && 'Pausado'}
                      {campaignProgress.status === 'completed' && 'Concluído'}
                      {campaignProgress.status === 'stopped' && 'Parado'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {campaignProgress.current_index} de {campaignProgress.total_customers}
                    </span>
                  </div>
                  
                  {/* Controles */}
                  <div className="flex items-center gap-2">
                    {campaignProgress.status === 'running' && (
                      <Button size="sm" variant="outline" onClick={pauseCampaign}>
                        <Pause className="w-4 h-4 mr-1" />
                        Pausar
                      </Button>
                    )}
                    {campaignProgress.status === 'paused' && (
                      <Button size="sm" variant="outline" onClick={resumeCampaign}>
                        <Play className="w-4 h-4 mr-1" />
                        Retomar
                      </Button>
                    )}
                    {['running', 'paused'].includes(campaignProgress.status) && (
                      <Button size="sm" variant="destructive" onClick={stopCampaign}>
                        <Square className="w-4 h-4 mr-1" />
                        Parar
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Barra de Progresso */}
                <div className="space-y-1">
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-500"
                      style={{ width: `${campaignProgress.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{campaignProgress.progress}%</span>
                    <span>
                      <span className="text-green-500">{campaignProgress.sent_count} enviados</span>
                      {campaignProgress.failed_count > 0 && (
                        <span className="text-red-500 ml-2">{campaignProgress.failed_count} falhas</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Lista de Destinatários */}
              <div className="border rounded-lg">
                <div className="p-3 border-b bg-muted/30">
                  <h4 className="text-sm font-medium">Destinatários</h4>
                </div>
                <div className="max-h-64 overflow-auto">
                  {campaignProgress.recipients?.map((recipient, idx) => (
                    <div 
                      key={recipient.customer_id || idx}
                      className={`flex items-center justify-between px-3 py-2 border-b last:border-0 ${
                        recipient.status === 'sent' ? 'bg-green-500/5' :
                        recipient.status === 'failed' ? 'bg-red-500/5' :
                        recipient.status === 'pending' && idx === campaignProgress.current_index ? 'bg-primary/10' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{recipient.name}</span>
                        <span className="text-xs text-muted-foreground">{recipient.phone}</span>
                      </div>
                      <div>
                        {recipient.status === 'pending' && idx === campaignProgress.current_index && (
                          <Badge variant="outline" className="text-xs">
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Enviando
                          </Badge>
                        )}
                        {recipient.status === 'pending' && idx !== campaignProgress.current_index && (
                          <Badge variant="outline" className="text-xs">Aguardando</Badge>
                        )}
                        {recipient.status === 'sent' && (
                          <Badge variant="success" className="text-xs bg-green-500/20 text-green-500">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Enviado
                          </Badge>
                        )}
                        {recipient.status === 'failed' && (
                          <Badge variant="destructive" className="text-xs">
                            <XCircle className="w-3 h-3 mr-1" />
                            Falha
                          </Badge>
                        )}
                        {recipient.status === 'skipped' && (
                          <Badge variant="secondary" className="text-xs">Pulado</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={closeCampaignProgress}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
